import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import Header from './Header';
import Footer from './Footer';
import { getLiveScorecardData } from '../api/liveScoringApi';
import {
  LiveScorecardV1, BattingEntryV1, BowlingEntryV1, FoWEntryV1, BallV1, MatchDropV1, YetToBatEntryV1, PlayerSummaryV1,
  OppositionWicketV1, OppositionBowlerDetailsV1, OppositionBallV1,
} from '../api/swaggerTypes';
import { getPlayerSummary } from '../api/statsApi';
import { getScoringArea } from '../utils/cricketUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

// ---------------------------------------------------------------------------
// Drop icon — shown inline after player name for each dropped catch
// ---------------------------------------------------------------------------

const DropIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 22"
    width="0.75em"
    height="1em"
    display="inline-block"
    aria-label="Dropped catch"
    role="img"
    style={{ verticalAlign: '-0.15em', flexShrink: 0 }}
  >
    <title>Dropped catch</title>
    {/* Cricket ball */}
    <circle cx="8" cy="7.5" r="6" fill="#ef4444" />
    {/* Seam — horizontal arc */}
    <path
      d="M2.5 6.5 Q8 11 13.5 6.5"
      fill="none"
      stroke="rgba(255,255,255,0.7)"
      strokeWidth="1.1"
      strokeLinecap="round"
    />
    {/* Seam — vertical arc */}
    <path
      d="M6.8 1.8 Q5 7.5 6.8 13.2"
      fill="none"
      stroke="rgba(255,255,255,0.7)"
      strokeWidth="1.1"
      strokeLinecap="round"
    />
    {/* Drop arrow — shaft */}
    <line x1="8" y1="15" x2="8" y2="19.5" stroke="#f59e0b" strokeWidth="1.8" strokeLinecap="round" />
    {/* Drop arrow — head */}
    <polyline
      points="5.2,17.2 8,20.5 10.8,17.2"
      fill="none"
      stroke="#f59e0b"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function dropsForPlayer(playerId: number | undefined, drops: MatchDropV1[] | null | undefined): number {
  if (!drops || playerId == null) return 0;
  return drops.find(d => d.playerId === playerId)?.drops ?? 0;
}

// ---------------------------------------------------------------------------
// BatterMiniProfile — expandable career stats row shown beneath a batter row
// ---------------------------------------------------------------------------

const BatterMiniProfile: React.FC<{ profile: PlayerSummaryV1; colSpan?: number }> = ({
  profile,
  colSpan = 7,
}) => {
  const avg = profile.battingAverage != null ? profile.battingAverage.toFixed(2) : '-';
  const chips: { label: string; value: string | number }[] = [
    { label: 'Caps', value: profile.matches ?? '-' },
    { label: 'Runs', value: profile.careerRuns ?? '-' },
    { label: 'Avg', value: avg },
    ...(profile.highScore != null ? [{ label: 'HS', value: profile.highScore }] : []),
    { label: 'Wkts', value: profile.careerWickets ?? '-' },
    ...(profile.bestBowling != null ? [{ label: 'BB', value: profile.bestBowling }] : []),
    ...(profile.debutYear != null ? [{ label: 'Debut', value: profile.debutYear }] : []),
  ];
  return (
    <tr className="bg-green-50 border-b border-green-100">
      <td colSpan={colSpan} className="px-3 py-3">
        <div className="flex items-center gap-3">
          {profile.imageUrl && (
            <img
              src={profile.imageUrl}
              alt={`${profile.firstName ?? ''} ${profile.surname ?? ''}`.trim()}
              className="w-14 h-14 rounded-full object-cover object-top flex-shrink-0 border-2 border-green-200"
            />
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-800 truncate">
              {`${profile.firstName ?? ''} ${profile.surname ?? ''}`.trim()}
            </p>
            {profile.playingRole && (
              <p className="text-xs text-gray-500 mb-1">{profile.playingRole}</p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-1">
              {chips.map(c => (
                <span
                  key={c.label}
                  className="inline-flex flex-col items-center bg-white border border-green-200 rounded px-1.5 py-0.5 text-center"
                >
                  <span className="text-xs font-semibold text-gray-800 leading-none">{c.value}</span>
                  <span className="text-[10px] text-gray-400 leading-none mt-0.5">{c.label}</span>
                </span>
              ))}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};

const ChevronDownIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    className={`inline-block w-3 h-3 ml-1 text-gray-400 transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`}
    fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const LoadingProfileRow: React.FC<{ colSpan?: number }> = ({ colSpan = 7 }) => (
  <tr className="bg-green-50 border-b border-green-100">
    <td colSpan={colSpan} className="px-3 py-3">
      <div className="flex items-center gap-3 animate-pulse">
        <div className="w-14 h-14 rounded-full bg-gray-200 flex-shrink-0" />
        <div className="flex-1 space-y-1.5">
          <div className="h-3 bg-gray-200 rounded w-1/3" />
          <div className="h-2 bg-gray-200 rounded w-1/4" />
          <div className="flex gap-1.5 mt-1">
            {[1,2,3,4,5].map(i => <div key={i} className="h-7 w-10 bg-gray-200 rounded" />)}
          </div>
        </div>
      </div>
    </td>
  </tr>
);

const LiveScorecard: React.FC = () => {
  const { matchId } = useParams<{ matchId: string }>();
  const [scorecardData, setScorecardData] = useState<LiveScorecardV1 | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeInnings, setActiveInnings] = useState<'our' | 'their'>('our');
  const [activeCommentaryTab, setActiveCommentaryTab] = useState<'vcc' | 'oppo'>('vcc');
  const [activeAnalysisTab, setActiveAnalysisTab] = useState<'worm' | 'manhattan' | 'partnerships' | 'wagon'>('worm');
  const [commentaryExpanded, setCommentaryExpanded] = useState(false);
  const [scorecardExpanded, setScorecardExpanded] = useState(false);
  const [analysisExpanded, setAnalysisExpanded] = useState(false);
  const [playerAnalysisExpanded, setPlayerAnalysisExpanded] = useState(false);
  // Opposition innings collapsible panels
  const [theirBattingExpanded, setTheirBattingExpanded] = useState(false);
  const [theirFowExpanded, setTheirFowExpanded] = useState(false);
  const [theirOverByOverExpanded, setTheirOverByOverExpanded] = useState(false);
  const [theirPartnershipsExpanded, setTheirPartnershipsExpanded] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [activePlayerAnalysisTab, setActivePlayerAnalysisTab] = useState<'worm' | 'wagon'>('worm');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [flashKeys, setFlashKeys] = useState<Set<string>>(new Set());
  const prevDataRef = useRef<LiveScorecardV1 | null>(null);
  const flashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mini-profile expand state
  const [expandedBatterIds, setExpandedBatterIds] = useState<Set<number>>(new Set());
  const [batterProfiles, setBatterProfiles] = useState<Map<number, PlayerSummaryV1>>(new Map());
  const [loadingBatterIds, setLoadingBatterIds] = useState<Set<number>>(new Set());

  const toggleBatterProfile = async (playerId: number) => {
    const wasExpanded = expandedBatterIds.has(playerId);
    setExpandedBatterIds(prev => {
      const next = new Set(prev);
      if (next.has(playerId)) { next.delete(playerId); } else { next.add(playerId); }
      return next;
    });
    if (!wasExpanded && !batterProfiles.has(playerId)) {
      setLoadingBatterIds(prev => new Set(prev).add(playerId));
      try {
        const profile = await getPlayerSummary(playerId);
        setBatterProfiles(prev => new Map(prev).set(playerId, profile));
      } catch {
        // Non-VCC player or API error — silently collapse
        setExpandedBatterIds(prev => { const n = new Set(prev); n.delete(playerId); return n; });
      } finally {
        setLoadingBatterIds(prev => { const n = new Set(prev); n.delete(playerId); return n; });
      }
    }
  };

  useEffect(() => {
    const fetchScorecardData = async (isPolling = false) => {
      if (!matchId) {
        setError('No match ID provided');
        setIsLoading(false);
        return;
      }

      try {
        if (!isPolling) setIsLoading(true);
        const data = await getLiveScorecardData(matchId);

        // Detect what changed on subsequent polls and build flash key set
        if (isPolling && prevDataRef.current) {
          const prev = prevDataRef.current.inPlayData;
          const next = data.inPlayData;
          const keys = new Set<string>();
          if (prev && next) {
            if (prev.score !== next.score || prev.wickets !== next.wickets) {
              keys.add('our-score');
            }
            if (prev.theirScore !== next.theirScore || prev.theirWickets !== next.theirWickets) {
              keys.add('their-score');
            }
            if (
              prev.onStrikeBatsman?.score !== next.onStrikeBatsman?.score ||
              prev.onStrikeBatsman?.balls !== next.onStrikeBatsman?.balls
            ) {
              keys.add('striker');
            }
            if (
              prev.otherBatsman?.score !== next.otherBatsman?.score ||
              prev.otherBatsman?.balls !== next.otherBatsman?.balls
            ) {
              keys.add('non-striker');
            }
            if ((prev.completedOvers?.length ?? 0) !== (next.completedOvers?.length ?? 0)) {
              keys.add('new-over');
            }
            if (
              prev.bowlerOneDetails?.details?.runs !== next.bowlerOneDetails?.details?.runs ||
              prev.bowlerOneDetails?.details?.wickets !== next.bowlerOneDetails?.details?.wickets
            ) {
              keys.add('bowler-1');
            }
            if (
              prev.bowlerTwoDetails?.details?.runs !== next.bowlerTwoDetails?.details?.runs ||
              prev.bowlerTwoDetails?.details?.wickets !== next.bowlerTwoDetails?.details?.wickets
            ) {
              keys.add('bowler-2');
            }
          }
          if (keys.size > 0) {
            if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
            setFlashKeys(keys);
            flashTimeoutRef.current = setTimeout(() => setFlashKeys(new Set()), 2200);
          }
        }

        prevDataRef.current = data;
        setScorecardData(data);
        setLastUpdated(new Date());

        const inPlay = data.inPlayData;
        // Auto-select the innings that's in progress or most recent (initial load only)
        if (!isPolling && inPlay) {
          if (inPlay.ourInningsStatus === 'InProgress') {
            setActiveInnings('our');
          } else if (inPlay.theirInningsStatus === 'InProgress') {
            setActiveInnings('their');
          } else if (inPlay.ourInningsStatus === 'Completed') {
            setActiveInnings('our');
          } else if (inPlay.theirInningsStatus === 'Completed') {
            setActiveInnings('their');
          }
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching scorecard data:', err);
        if (!isPolling) {
          setError(err instanceof Error ? err.message : 'Failed to load scorecard');
          setScorecardData(null);
        }
      } finally {
        if (!isPolling) setIsLoading(false);
      }
    };

    fetchScorecardData(false);

    const intervalId = setInterval(() => {
      const d = prevDataRef.current;
      const isMatchLive =
        d?.inPlayData?.ourInningsStatus === 'InProgress' ||
        d?.inPlayData?.theirInningsStatus === 'InProgress';
      if (isMatchLive) {
        fetchScorecardData(true);
      }
    }, 30_000);

    return () => {
      clearInterval(intervalId);
      if (flashTimeoutRef.current) clearTimeout(flashTimeoutRef.current);
    };
  }, [matchId]);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'TBC';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const isLive = (data: LiveScorecardV1): boolean => {
    return data.inPlayData?.ourInningsStatus === 'InProgress' ||
           data.inPlayData?.theirInningsStatus === 'InProgress';
  };

  const isCompleted = (data: LiveScorecardV1): boolean => {
    const inPlay = data.inPlayData;
    const final = data.finalScorecard;

    return (!!inPlay && inPlay.ourInningsStatus === 'Completed' && inPlay.theirInningsStatus === 'Completed') ||
      ((final?.ourInnings?.batting?.entries?.length || 0) > 0);
  };

  const formatDismissal = (entry: BattingEntryV1): string => {
    const wicket = entry.wicket;
    if (!wicket) return 'not out';
    if (wicket.isCaughtAndBowled) return `c&b ${wicket.bowler ?? ''}`.trim();
    if (wicket.isCaught) return `ct. ${wicket.fielder || 'unknown'} b. ${wicket.bowler ?? ''}`.trim();
    if (wicket.isBowled) return `b. ${wicket.bowler ?? ''}`.trim();
    if (wicket.isLbw) return `lbw b. ${wicket.bowler ?? ''}`.trim();
    if (wicket.isStumped) return `st. ${wicket.fielder || 'unknown'} b. ${wicket.bowler ?? ''}`.trim();
    if (wicket.isRunOut) return `run out (${wicket.fielder || 'unknown'})`;
    if (wicket.isHitWicket) return 'hit wicket';
    if (wicket.isRetiredHurt) return 'retired hurt';
    if (wicket.isRetired) return 'retired';
    return 'not out';
  };

  const formatWicketDismissal = (wicket: NonNullable<BallV1['wicket']>): string => {
    if (wicket.isCaughtAndBowled) return `c&b ${wicket.bowler ?? ''}`.trim();
    if (wicket.isCaught) return `ct. ${wicket.fielder || 'unknown'} b. ${wicket.bowler ?? ''}`.trim();
    if (wicket.isBowled) return `b. ${wicket.bowler ?? ''}`.trim();
    if (wicket.isLbw) return `lbw b. ${wicket.bowler ?? ''}`.trim();
    if (wicket.isStumped) return `st. ${wicket.fielder || 'unknown'} b. ${wicket.bowler ?? ''}`.trim();
    if (wicket.isRunOut) return `run out (${wicket.fielder || 'unknown'})`;
    if (wicket.isHitWicket) return 'hit wicket';
    if (wicket.isRetiredHurt) return 'retired hurt';
    if (wicket.isRetired) return 'retired';
    return 'out';
  };

  /**
   * Format an OppositionWicketV1 dismissal using the VCC player map for name resolution.
   * bowlerPlayerId and fielderPlayerId are resolved from vccPlayerMap.
   */
  const formatOppositionDismissal = (
    wicket: OppositionWicketV1 | null | undefined,
    playerMap: Map<number, string>,
    inningsComplete: boolean,
  ): string => {
    if (!wicket) return inningsComplete ? 'not out' : 'batting';
    const bowler = wicket.bowlerPlayerId != null
      ? (playerMap.get(wicket.bowlerPlayerId) ?? undefined)
      : undefined;
    const fielder = wicket.fielderPlayerId != null
      ? (playerMap.get(wicket.fielderPlayerId) ?? undefined)
      : undefined;
    const mode = (wicket.modeOfDismissal ?? '').toLowerCase();
    switch (mode) {
      case 'bowled':      return `b ${bowler ?? ''}`.trim();
      case 'caught':      return fielder ? `c ${fielder} b ${bowler ?? ''}` : `c&b ${bowler ?? ''}`;
      case 'c&b':         return `c&b ${bowler ?? ''}`.trim();
      case 'lbw':         return `lbw b ${bowler ?? ''}`.trim();
      case 'stumped':     return fielder ? `st ${fielder} b ${bowler ?? ''}` : `st b ${bowler ?? ''}`;
      case 'run out':     return fielder ? `run out (${fielder})` : 'run out';
      case 'hit wicket':  return bowler ? `hit wicket b ${bowler}` : 'hit wicket';
      case 'retired':     return 'retired';
      case 'retired hurt': return 'retired hurt';
      default: return wicket.description ?? 'out';
    }
  };

  const getOppositionBallBlob = (ball: OppositionBallV1): { label: string; className: string } => {
    if (ball.wicket) return { label: 'W', className: 'bg-red-600 text-white' };
    if (ball.isWide) {
      const amount = ball.amount ?? 0;
      return { label: amount > 1 ? `${amount}Wd` : 'Wd', className: 'bg-yellow-400 text-gray-800' };
    }
    if (ball.isNoBall) {
      const amount = ball.amount ?? 0;
      return { label: amount > 1 ? `${amount}Nb` : 'Nb', className: 'bg-yellow-400 text-gray-800' };
    }
    const amount = ball.amount ?? 0;
    if (amount === 0) return { label: '·', className: 'bg-gray-300 text-gray-600' };
    if (ball.isSix || amount === 6) return { label: '6', className: 'bg-orange-500 text-white' };
    if ((ball.isBoundary && !ball.isSix) || amount === 4) return { label: '4', className: 'bg-blue-500 text-white' };
    return { label: String(amount), className: 'bg-gray-200 text-gray-700' };
  };

  const getBallDescription = (ball: BallV1): string => {
    if (ball.wicket) return 'OUT!';
    const amount = ball.amount ?? 0;
    const thing = ball.thing ?? '';
    const plural = amount !== 1 ? 's' : '';
    const area = ball.angle != null ? getScoringArea(ball.angle) : null;
    switch (thing) {
      case '':
        switch (amount) {
          case 0: return 'no run';
          case 1: return area ? `single to ${area}` : 'single';
          case 4: return area ? `FOUR through ${area}` : 'FOUR';
          case 6: return area ? `SIX! over ${area}` : 'SIX!';
          default: return area ? `${amount} runs to ${area}` : `${amount} runs`;
        }
      case 'wd': return `${amount} wide${plural}`;
      case 'nb': return `${amount} no ball${plural}`;
      case 'b': return `${amount} bye${plural}`;
      case 'lb': return `${amount} leg bye${plural}`;
      default: return `${amount} ${thing}`;
    }
  };

  const toOrdinal = (n: number): string => {
    const lastTwo = n % 100;
    const lastOne = n % 10;
    if (lastTwo >= 11 && lastTwo <= 13) return `${n}th`;
    if (lastOne === 1) return `${n}st`;
    if (lastOne === 2) return `${n}nd`;
    if (lastOne === 3) return `${n}rd`;
    return `${n}th`;
  };

  const getBallBlob = (ball: BallV1): { label: string; className: string } => {
    if (ball.wicket) return { label: 'W', className: 'bg-red-600 text-white' };
    const thing = ball.thing ?? '';
    const amount = ball.amount ?? 0;
    if (thing === 'wd') return { label: amount > 1 ? `${amount}Wd` : 'Wd', className: 'bg-yellow-400 text-gray-800' };
    if (thing === 'nb') return { label: amount > 1 ? `${amount}Nb` : 'Nb', className: 'bg-yellow-400 text-gray-800' };
    if (thing === 'b') return { label: amount > 1 ? `${amount}B` : 'B', className: 'bg-yellow-400 text-gray-800' };
    if (thing === 'lb') return { label: amount > 1 ? `${amount}Lb` : 'Lb', className: 'bg-yellow-400 text-gray-800' };
    if (amount === 0) return { label: '·', className: 'bg-gray-300 text-gray-600' };
    if (ball.isSix || amount === 6) return { label: '6', className: 'bg-orange-500 text-white' };
    if ((ball.isBoundary && !ball.isSix) || amount === 4) return { label: '4', className: 'bg-blue-500 text-white' };
    return { label: String(amount), className: 'bg-gray-200 text-gray-700' };
  };

  const formatBattingStats = (
    score: number | undefined,
    details: { balls?: number; fours?: number; sixes?: number; strikeRate?: number } | null | undefined
  ): string => {
    if (score == null) return '';
    if (!details) return ` ${score}`;
    return ` ${score} (${details.balls ?? 0}b ${details.fours ?? 0}x4 ${details.sixes ?? 0}x6) SR: ${(details.strikeRate ?? 0).toFixed(2)}`;
  };

  const renderBattingTable = (
    entries: BattingEntryV1[],
    extras?: { wides?: number; noBalls?: number; byes?: number; legByes?: number; penalties?: number; total?: number },
    score?: number,
    wickets?: number,
    drops?: MatchDropV1[] | null
  ) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
            <th className="py-2 pr-2">Batter</th>
            <th className="py-2 text-gray-400 font-normal hidden sm:table-cell"></th>
            <th className="py-2 text-right">R</th>
            <th className="py-2 text-right">B</th>
            <th className="py-2 text-right">4s</th>
            <th className="py-2 text-right">6s</th>
            <th className="py-2 text-right">SR</th>
          </tr>
        </thead>
        <tbody className="text-gray-800">
          {entries.map((entry, index) => {
            const dropCount = dropsForPlayer(entry.playerId, drops);
            const isExpandable = !!entry.playerId;
            const isExpanded = isExpandable && expandedBatterIds.has(entry.playerId!);
            const isLoadingProfile = isExpandable && loadingBatterIds.has(entry.playerId!);
            const profile = isExpandable ? batterProfiles.get(entry.playerId!) : undefined;
            return (
              <React.Fragment key={index}>
                <tr
                  className={`border-b border-gray-100 ${isExpandable ? 'cursor-pointer hover:bg-gray-50 select-none' : 'hover:bg-gray-50'}`}
                  onClick={isExpandable ? () => toggleBatterProfile(entry.playerId!) : undefined}
                >
                  <td className="py-2 pr-2 font-medium">
                    <span className="inline-flex items-center gap-0.5">
                      {entry.playerName}
                      {isExpandable && <ChevronDownIcon expanded={isExpanded} />}
                    </span>
                    {dropCount > 0 && (
                      <span
                        className="ml-1 inline-flex gap-0.5"
                        title={`Dropped ${dropCount} ${dropCount === 1 ? 'catch' : 'catches'}`}
                        aria-label={`Dropped ${dropCount} ${dropCount === 1 ? 'catch' : 'catches'}`}
                      >
                        {Array.from({ length: dropCount }).map((_, i) => (
                          <DropIcon key={i} />
                        ))}
                      </span>
                    )}
                    <span className="block text-xs text-gray-400 sm:hidden">{formatDismissal(entry)}</span>
                  </td>
                  <td className="py-2 text-sm text-gray-500 hidden sm:table-cell">{formatDismissal(entry)}</td>
                  <td className="py-2 text-right font-medium">{entry.runs ?? 0}</td>
                  <td className="py-2 text-right text-gray-600">{entry.ballsFaced ?? 0}</td>
                  <td className="py-2 text-right text-gray-600">{entry.fours ?? 0}</td>
                  <td className="py-2 text-right text-gray-600">{entry.sixes ?? 0}</td>
                  <td className="py-2 text-right text-gray-600">
                    {entry.ballsFaced ? ((entry.runs ?? 0) / entry.ballsFaced * 100).toFixed(1) : '-'}
                  </td>
                </tr>
                {isExpanded && isLoadingProfile && <LoadingProfileRow colSpan={7} />}
                {isExpanded && !isLoadingProfile && profile && <BatterMiniProfile profile={profile} colSpan={7} />}
              </React.Fragment>
            );
          })}
          {extras && (
            <tr className="border-b border-gray-100 text-gray-600">
              <td className="py-2 pr-2 font-medium" colSpan={2}>
                Extras
                <span className="text-xs ml-2 text-gray-400">
                  (b {extras.byes ?? 0}, lb {extras.legByes ?? 0}, w {extras.wides ?? 0}, nb {extras.noBalls ?? 0}
                  {(extras.penalties ?? 0) > 0 ? `, pen ${extras.penalties}` : ''})
                </span>
              </td>
              <td className="py-2 text-right" colSpan={5}>{extras.total ?? 0}</td>
            </tr>
          )}
          {score !== undefined && (
            <tr className="border-t-2 border-gray-200 font-semibold text-gray-900">
              <td className="py-2 pr-2" colSpan={2}>Total</td>
              <td className="py-2 text-right" colSpan={5}>
                {score}/{wickets ?? entries.filter(e => e.wicket).length}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  const renderBowlingTable = (entries: BowlingEntryV1[]) => (
    <div className="mt-8 overflow-x-auto">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Bowling</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2 border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
            <th className="py-2">Bowler</th>
            <th className="py-2 text-right">O</th>
            <th className="py-2 text-right">M</th>
            <th className="py-2 text-right">R</th>
            <th className="py-2 text-right">W</th>
            <th className="py-2 text-right">Econ</th>
          </tr>
        </thead>
        <tbody className="text-gray-800">
          {entries.map((entry, index) => (
            <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
              <td className="py-2 font-medium">{entry.playerName}</td>
              <td className="py-2 text-right text-gray-600">{entry.overs}</td>
              <td className="py-2 text-right text-gray-600">{entry.maidens ?? 0}</td>
              <td className="py-2 text-right text-gray-600">{entry.runs ?? 0}</td>
              <td className="py-2 text-right font-medium">{entry.wickets ?? 0}</td>
              <td className="py-2 text-right text-gray-600">
                {entry.overs ? ((entry.runs ?? 0) / (entry.overs as number)).toFixed(2) : '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderFallOfWickets = (fowEntries: FoWEntryV1[]) => {
    if (!fowEntries || fowEntries.length === 0) return null;
    return (
      <div className="mt-6">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Fall of Wickets</h3>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-700">
          {fowEntries.map((fow, index) => (
            <span key={index} className="whitespace-nowrap">
              {fow.score ?? 0}-{fow.wicket ?? (index + 1)}
              {fow.outgoingPlayer?.name && (
                <span className="text-gray-500">
                  {' '}({fow.outgoingPlayer.name}{fow.overs != null ? `, ${fow.overs}ov` : ''})
                </span>
              )}
            </span>
          ))}
        </div>
      </div>
    );
  };

  const renderInningsContent = (
    innings: NonNullable<NonNullable<LiveScorecardV1['finalScorecard']>['ourInnings']>,
    teamName: string,
    teamIcon: React.ReactNode,
    drops?: MatchDropV1[] | null
  ) => (
    <div>
      <div className="flex items-center gap-3 mb-5">
        {teamIcon}
        <div>
          <h2 className="text-lg font-semibold">{teamName} Innings</h2>
          <p className="text-sm text-gray-500">
            {innings.batting?.score ?? 0}/{innings.batting?.wickets ?? 0}
            {innings.inningsLength ? ` (${innings.inningsLength} ov)` : ''}
          </p>
        </div>
      </div>
      {renderBattingTable(
        innings.batting?.entries || [],
        innings.batting?.extras,
        innings.batting?.score,
        innings.batting?.wickets,
        drops
      )}
      {innings.fow?.entries && renderFallOfWickets(innings.fow.entries)}
      {innings.bowling?.entries && innings.bowling.entries.length > 0 &&
        renderBowlingTable(innings.bowling.entries)}
    </div>
  );

  if (isLoading) {
    return (
      <div className="font-sans text-villageText bg-gray-50 min-h-screen">
        <Header />
        <main className="max-w-6xl mx-auto px-4 py-10">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-2/3"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="font-sans text-villageText bg-gray-50 min-h-screen">
        <Header />
        <main className="max-w-6xl mx-auto px-4 py-10">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-red-800 mb-2">Error Loading Scorecard</h2>
            <p className="text-red-700">{error}</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!scorecardData) {
    return (
      <div className="font-sans text-villageText bg-gray-50 min-h-screen">
        <Header />
        <main className="max-w-6xl mx-auto px-4 py-10">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <p className="text-blue-700">No scorecard data available for this match.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const live = isLive(scorecardData);
  const completed = isCompleted(scorecardData);
  const villageIsHome = scorecardData.matchData?.isHome !== false;
  const oppositionIsHomeTeam = completed && !villageIsHome;

  if (!scorecardData.inPlayData && !completed) {
    return (
      <div className="font-sans text-villageText bg-gray-50 min-h-screen">
        <Header />
        <main className="max-w-6xl mx-auto px-4 py-10">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <p className="text-blue-700">Match data is not yet available.</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const data = scorecardData.inPlayData!;

  const fowByPlayerId = new Map(
    (data.fallOfWickets ?? []).map(f => [f.outGoingPlayerId, f])
  );

  // Scores to show in the hero header
  const ourScoreDisplay = completed
    ? `${scorecardData.finalScorecard?.ourInnings?.batting?.score ?? data.score ?? 0}/${scorecardData.finalScorecard?.ourInnings?.batting?.wickets ?? data.wickets ?? 0}`
    : (live && (data.ourInningsStatus === 'InProgress' || data.ourInningsStatus === 'Completed'))
      ? `${data.score ?? 0}/${data.wickets ?? 0}`
      : null;

  const theirScoreDisplay = completed
    ? `${scorecardData.finalScorecard?.theirInnings?.batting?.score ?? data.theirScore ?? 0}/${scorecardData.finalScorecard?.theirInnings?.batting?.wickets ?? data.theirWickets ?? 0}`
    : (live && (data.theirInningsStatus === 'InProgress' || data.theirInningsStatus === 'Completed'))
      ? `${data.theirScore ?? 0}/${data.theirWickets ?? 0}`
      : null;

  const hasOurInnings = completed
    ? (scorecardData.finalScorecard?.ourInnings?.batting?.entries?.length ?? 0) > 0
    : false;
  const hasTheirInnings = completed
    ? (scorecardData.finalScorecard?.theirInnings?.batting?.entries?.length ?? 0) > 0
    : false;
  const hasBothInnings = hasOurInnings && hasTheirInnings;

  const villageIcon = (size: string) => (
    <img src="/images/vcc_cricle_small.png" className={`${size} flex-shrink-0`} alt="The Village CC" />
  );
  const oppositionLogoUrl = scorecardData.matchData?.opposition?.logoUrl;
  const oppositionIcon = (size: string) =>
    oppositionLogoUrl ? (
      <img src={oppositionLogoUrl} className={`${size} rounded-full object-contain flex-shrink-0`} alt={data.opposition ?? 'Opposition'} />
    ) : (
      <div className={`${size} rounded-full border-2 border-gray-400 flex items-center justify-center flex-shrink-0`}>
        <span className="text-gray-600 font-semibold text-xs sm:text-base">
          {(data.opposition || '').substring(0, 2).toUpperCase()}
        </span>
      </div>
    );

  // Section variables for conditional ordering
  const commentarySection = ((data.completedOvers?.length ?? 0) > 0 || (data.theirCompletedOvers?.length ?? 0) > 0) ? (
    <section className="max-w-6xl mx-auto mt-4">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <button
          type="button"
          className="w-full flex justify-between items-center px-4 py-3 text-left"
          onClick={() => setCommentaryExpanded(prev => !prev)}
          aria-expanded={commentaryExpanded}
        >
          <span className="text-sm font-semibold text-gray-700">Over-by-over Commentary</span>
          <svg
            className={`w-4 h-4 text-gray-400 transition-transform ${commentaryExpanded ? 'rotate-180' : ''}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
        {commentaryExpanded && (
          <div className="border-t border-gray-100 px-4 py-4">
            <div className="flex gap-2 mb-3 flex-wrap">
              {(data.completedOvers?.length ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={() => setActiveCommentaryTab('vcc')}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    activeCommentaryTab === 'vcc'
                      ? 'bg-villageGreen text-white'
                      : 'border border-villageGreen text-villageGreen hover:bg-villageGreenLight'
                  }`}
                >
                  VCC Commentary
                </button>
              )}
              {(data.theirCompletedOvers?.length ?? 0) > 0 && (
                <button
                  type="button"
                  onClick={() => setActiveCommentaryTab('oppo')}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    activeCommentaryTab === 'oppo'
                      ? 'bg-villageGreen text-white'
                      : 'border border-villageGreen text-villageGreen hover:bg-villageGreenLight'
                  }`}
                >
                  Oppo Commentary
                </button>
              )}
            </div>
            {activeCommentaryTab === 'vcc' && (data.completedOvers?.length ?? 0) > 0 && (
              <div>
                {data.ourInningsCommentary && (
                  <p className="mb-3 text-sm text-gray-600 italic">{data.ourInningsCommentary}</p>
                )}
                {[...(data.completedOvers ?? [])].reverse().map((over, i, arr) => {
                  const overNum = over.over?.overNumber ?? (arr.length - i);
                  const balls = over.over?.balls
                    ? [...over.over.balls].sort((a, b) => (a.ballNumber ?? 0) - (b.ballNumber ?? 0))
                    : [];
                  return (
                    <div key={i} className={`py-4 text-sm ${i < arr.length - 1 ? 'border-b border-gray-100' : ''}`}>
                      <div className="bg-gray-50 rounded-lg px-3 py-2 mb-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">{`Over ${overNum}`}</span>
                            {over.over?.bowler && (
                              <span className="text-gray-500">· {over.over.bowler}</span>
                            )}
                          </div>
                          <span className="text-gray-600 text-xs">
                            Village {over.scoreAtEndOfOver ?? 0}/{over.wicketsAtEndOfOver ?? 0}
                            <span className="ml-1 text-gray-400">(+{over.scoreForThisOver ?? 0})</span>
                          </span>
                        </div>
                        {over.over?.commentary && (
                          <p className="mt-1 text-xs text-gray-600 italic">{over.over.commentary}</p>
                        )}
                      </div>
                      {balls.length > 0 && (
                        <div className="space-y-2 pl-1" aria-label={`Over ${overNum} deliveries`}>
                          {balls.map((ball, bi) => {
                            const blob = getBallBlob(ball);
                            const fow = ball.wicket ? fowByPlayerId.get(ball.wicket.player) : undefined;
                            const statsText = formatBattingStats(fow?.outGoingPlayerScore, fow?.outgoingBatsmanInningsDetails);
                            return (
                              <div key={bi}>
                                <div className="flex items-center gap-2">
                                  <span
                                    data-testid="ball-blob"
                                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${blob.label.length >= 3 ? 'text-[9px]' : 'text-xs'} font-bold flex-shrink-0 ${blob.className}`}
                                  >
                                    {blob.label}
                                  </span>
                                  <div className="text-gray-600">
                                    <span className="font-mono text-xs text-gray-400 mr-1">{overNum}.{ball.ballNumber}</span>
                                    {ball.bowler && ball.batsmanName ? (
                                      <>
                                        {`${ball.bowler} to ${ball.batsmanName}, `}
                                        {ball.wicket ? <span className="font-bold text-red-700">OUT!</span> : getBallDescription(ball)}
                                      </>
                                    ) : (
                                      ball.wicket ? <span className="font-bold text-red-700">OUT!</span> : getBallDescription(ball)
                                    )}
                                  </div>
                                </div>
                                {ball.wicket && (
                                  <div className="ml-9 mt-1 bg-red-50 border border-red-100 rounded px-2 py-1 text-sm">
                                    {ball.wicket.playerName && (
                                      <div className="font-semibold text-gray-800">
                                        {ball.wicket.playerName} {formatWicketDismissal(ball.wicket)}{statsText}
                                      </div>
                                    )}
                                    {ball.wicket.description && (
                                      <div className="text-gray-500 italic text-xs mt-0.5">{ball.wicket.description}</div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {activeCommentaryTab === 'oppo' && (data.theirCompletedOvers?.length ?? 0) > 0 && (
              <div>
                {data.theirInningsCommentary && (
                  <p className="mb-3 text-sm text-gray-600 italic">{data.theirInningsCommentary}</p>
                )}
                {[...(data.theirCompletedOvers ?? [])].reverse().map((over, i, arr) => (
                  <div key={i} className={`py-3 text-sm ${i < arr.length - 1 ? 'border-b border-gray-100' : ''}`}>
                    <span className="font-medium">{`Over ${over.over ?? (arr.length - i)}`}</span>
                    <span className="ml-2 text-gray-500">
                      {data.opposition} {over.score ?? 0}/{over.wickets ?? 0}
                    </span>
                    {over.commentary && (
                      <p className="mt-1 text-gray-600">{over.commentary}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  ) : null;

  // Scorecard section — collapsible, works for both live and completed matches
  const scorecardSection = (() => {
    // For completed matches use the formal final scorecard data
    if (completed) {
      if (!hasOurInnings && !hasTheirInnings) return null;
      const ourInningsContent = hasOurInnings && scorecardData.finalScorecard?.ourInnings
        ? renderInningsContent(scorecardData.finalScorecard.ourInnings, 'The Village CC', villageIcon('h-8 w-8'), scorecardData.finalScorecard.drops)
        : null;
      const theirInningsContent = hasTheirInnings && scorecardData.finalScorecard?.theirInnings
        ? renderInningsContent(scorecardData.finalScorecard.theirInnings, data.opposition ?? 'Opposition', oppositionIcon('h-8 w-8'))
        : null;
      return (
        <section className="max-w-6xl mx-auto mt-4">
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
            <button
              type="button"
              className="w-full flex justify-between items-center px-4 py-3 text-left"
              onClick={() => setScorecardExpanded(prev => !prev)}
              aria-expanded={scorecardExpanded}
            >
              <span className="text-sm font-semibold text-gray-700">Scorecards</span>
              <svg
                className={`w-4 h-4 text-gray-400 transition-transform ${scorecardExpanded ? 'rotate-180' : ''}`}
                fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {scorecardExpanded && (
              <div className="border-t border-gray-100 px-4 py-4">
                {hasBothInnings && (
                  <div className="flex gap-2 mb-4 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setActiveInnings('our')}
                      className={`px-4 py-2 rounded-full text-sm font-medium ${
                        activeInnings === 'our'
                          ? 'bg-villageGreen text-white'
                          : 'border border-villageGreen text-villageGreen hover:bg-villageGreenLight'
                      }`}
                    >
                      The Village CC Innings
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveInnings('their')}
                      className={`px-4 py-2 rounded-full text-sm font-medium ${
                        activeInnings === 'their'
                          ? 'bg-villageGreen text-white'
                          : 'border border-villageGreen text-villageGreen hover:bg-villageGreenLight'
                      }`}
                    >
                      {data.opposition ?? 'Opposition'} Innings
                    </button>
                  </div>
                )}
                {(!hasBothInnings || activeInnings === 'our') && ourInningsContent}
                {(!hasBothInnings || activeInnings === 'their') && theirInningsContent}
              </div>
            )}
          </div>
        </section>
      );
    }

    // For live matches use ball-by-ball aggregated data
    if (!live) return null;

    const ourStatus = data.ourInningsStatus;
    const theirStatus = data.theirInningsStatus;

    // Build aggregated batting stats from completed overs ball-by-ball data
    const buildInPlayBattingRows = () => {
      const completedOvers = data.completedOvers ?? [];
      type BatsmanRow = {
        id: number; name: string; runs: number; balls: number;
        fours: number; sixes: number; isOut: boolean; battingOrder: number;
        dismissal?: string;
      };
      const batsmenMap = new Map<number, BatsmanRow>();
      let orderCounter = 0;

      completedOvers.forEach(over => {
        over.over?.balls?.forEach(ball => {
          if (ball.batsman == null) return;
          if (!batsmenMap.has(ball.batsman)) {
            batsmenMap.set(ball.batsman, {
              id: ball.batsman,
              name: ball.batsmanName ?? `Batsman ${orderCounter + 1}`,
              runs: 0, balls: 0, fours: 0, sixes: 0,
              isOut: false, battingOrder: orderCounter++,
            });
          }
          const entry = batsmenMap.get(ball.batsman)!;
          const thing = ball.thing ?? '';
          // Balls faced: everything except wides
          if (thing !== 'wd') entry.balls++;
          // Bat runs: normal deliveries and no-balls (no-ball penalty = 1 run, so bat runs = total - 1)
          if (thing === '' || thing === 'nb') {
            const batRuns = thing === 'nb' ? Math.max(0, (ball.amount ?? 0) - 1) : (ball.amount ?? 0);
            entry.runs += batRuns;
            // Prefer isSix/isBoundary flags; fall back to run count
            if (ball.isSix || batRuns >= 6) entry.sixes++;
            else if ((ball.isBoundary && !ball.isSix) || batRuns === 4) entry.fours++;
          }
          if (ball.wicket) {
            // Use ball.wicket.player to identify the dismissed batsman — this may differ
            // from ball.batsman (the facing batter) e.g. in run outs of the non-striker
            // or any case where the dismissed player is not the one who faced the ball.
            const dismissedId = ball.wicket.player ?? ball.batsman;
            const dismissedEntry = batsmenMap.get(dismissedId) ?? entry;
            dismissedEntry.isOut = true;
            dismissedEntry.dismissal = formatWicketDismissal(ball.wicket);
          }
        });
      });

      // Override current batsmen with live aggregated stats (these include the current in-progress over)
      const updateWithLiveBatsman = (liveBatsman: NonNullable<typeof data.onStrikeBatsman>) => {
        if (liveBatsman.playerId == null) return;
        const existing = batsmenMap.get(liveBatsman.playerId);
        if (existing) {
          existing.runs = liveBatsman.score ?? existing.runs;
          existing.balls = liveBatsman.balls ?? existing.balls;
          existing.fours = liveBatsman.fours ?? existing.fours;
          existing.sixes = liveBatsman.sixes ?? existing.sixes;
          // This player is confirmed as currently batting — clear any incorrect 'out' status
          // that may have been set due to a data inconsistency in the completed overs
          existing.isOut = false;
          existing.dismissal = undefined;
        } else {
          // Batsman appeared in the current (incomplete) over — add them
          batsmenMap.set(liveBatsman.playerId, {
            id: liveBatsman.playerId,
            name: liveBatsman.name ?? 'Unknown',
            runs: liveBatsman.score ?? 0,
            balls: liveBatsman.balls ?? 0,
            fours: liveBatsman.fours ?? 0,
            sixes: liveBatsman.sixes ?? 0,
            isOut: false,
            battingOrder: orderCounter++,
          });
        }
      };

      if (data.onStrikeBatsman) updateWithLiveBatsman(data.onStrikeBatsman);
      if (data.otherBatsman) updateWithLiveBatsman(data.otherBatsman);

      return Array.from(batsmenMap.values()).sort((a, b) => a.battingOrder - b.battingOrder);
    };

    const battingRows = (ourStatus === 'InProgress' || ourStatus === 'Completed') ? buildInPlayBattingRows() : [];

    // Bowling rows: use liveBowlingCard (all bowlers) or fall back to bowlerOneDetails/bowlerTwoDetails
    const bowlingRows: Array<{ name: string; overs?: number; maidens?: number; runs?: number; wickets?: number; economy?: number }> =
      (data.liveBowlingCard && data.liveBowlingCard.length > 0)
        ? data.liveBowlingCard.map(b => ({
            name: b.name ?? '',
            overs: b.details?.overs,
            maidens: b.details?.maidens,
            runs: b.details?.runs,
            wickets: b.details?.wickets,
            economy: b.details?.economy,
          }))
        : [data.bowlerOneDetails, data.bowlerTwoDetails]
            .filter((b): b is NonNullable<typeof b> => !!b)
            .map(b => ({
              name: b.name ?? '',
              overs: b.details?.overs,
              maidens: b.details?.maidens,
              runs: b.details?.runs,
              wickets: b.details?.wickets,
              economy: b.details?.economy,
            }));

    const fowEntries = data.fallOfWickets ?? [];

    const onStrikeId = data.onStrikeBatsman?.playerId;
    const otherBatsmanId = data.otherBatsman?.playerId;

    const ourInningsContent = (() => {
      if (ourStatus === 'NotStarted' || !ourStatus) {
        return (
          <div className="py-6 text-center text-sm text-gray-500">
            The Village CC innings has not yet started.
          </div>
        );
      }
      return (
        <div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="py-2 pr-2">Batter</th>
                  <th className="py-2 text-gray-400 font-normal"></th>
                  <th className="py-2 text-right">R</th>
                  <th className="py-2 text-right">B</th>
                  <th className="py-2 text-right">4s</th>
                  <th className="py-2 text-right">6s</th>
                  <th className="py-2 text-right">SR</th>
                </tr>
              </thead>
              <tbody className="text-gray-800">
                {battingRows.map((row, idx) => {
                  const isOnStrike = row.id === onStrikeId;
                  const isOther = row.id === otherBatsmanId;
                  const isBatting = isOnStrike || isOther;
                  const isExpandable = !!row.id;
                  const isExpanded = isExpandable && expandedBatterIds.has(row.id);
                  const isLoadingProfile = isExpandable && loadingBatterIds.has(row.id);
                  const profile = isExpandable ? batterProfiles.get(row.id) : undefined;
                  return (
                    <React.Fragment key={idx}>
                      <tr
                        className={`border-b border-gray-100 ${isExpandable ? 'cursor-pointer hover:bg-gray-50 select-none' : 'hover:bg-gray-50'}`}
                        onClick={isExpandable ? () => toggleBatterProfile(row.id) : undefined}
                      >
                        <td className="py-2 pr-2 font-medium">
                          <span className="inline-flex items-center gap-0.5">
                            {row.name}
                            {isOnStrike && <span className="ml-1 text-villageGreen text-xs font-bold">*</span>}
                            {isBatting && !row.isOut && (
                              <span className="ml-1" role="img" aria-label="batting">🏏</span>
                            )}
                            {isExpandable && <ChevronDownIcon expanded={isExpanded} />}
                          </span>
                          {row.isOut && row.dismissal && (
                            <span className="block text-xs text-gray-400 font-normal mt-0.5">{row.dismissal}</span>
                          )}
                        </td>
                        <td className="py-2 text-right font-medium">{row.runs}</td>
                        <td className="py-2 text-right text-gray-600">{row.balls}</td>
                        <td className="py-2 text-right text-gray-600">{row.fours}</td>
                        <td className="py-2 text-right text-gray-600">{row.sixes}</td>
                        <td className="py-2 text-right text-gray-600">
                          {row.balls > 0 ? (row.runs / row.balls * 100).toFixed(1) : '-'}
                        </td>
                      </tr>
                      {isExpanded && isLoadingProfile && <LoadingProfileRow colSpan={7} />}
                      {isExpanded && !isLoadingProfile && profile && <BatterMiniProfile profile={profile} colSpan={7} />}
                    </React.Fragment>
                  );
                })}
                {battingRows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-4 text-center text-gray-400">No batting data yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {fowEntries.length > 0 && (
            <div className="mt-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Fall of Wickets</h3>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-700">
                {fowEntries.map((fow, i) => (
                  <span key={i} className="whitespace-nowrap">
                    {fow.teamScore ?? 0}-{fow.wicketNumber ?? (i + 1)}
                    {fow.outgoingPlayerName && (
                      <span className="text-gray-500"> ({fow.outgoingPlayerName}{fow.overAsString ? `, ${fow.overAsString}` : ''})</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          )}

          {(data.yetToBat?.length ?? 0) > 0 && ourStatus === 'InProgress' && (
            <div className="mt-4">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Yet to Bat</h3>
              <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm text-gray-700">
                {data.yetToBat!.map((entry: YetToBatEntryV1, i: number) => (
                  <span key={i} className="whitespace-nowrap text-gray-600">{entry.playerName}</span>
                ))}
              </div>
            </div>
          )}

          {bowlingRows.length > 0 && (
            <div className="mt-6 overflow-x-auto">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Bowling</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                    <th className="py-2">Bowler</th>
                    <th className="py-2 text-right">O</th>
                    <th className="py-2 text-right">M</th>
                    <th className="py-2 text-right">R</th>
                    <th className="py-2 text-right">W</th>
                    <th className="py-2 text-right">Econ</th>
                  </tr>
                </thead>
                <tbody className="text-gray-800">
                  {bowlingRows.map((bowler, i) => (
                    <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 font-medium">{bowler.name}</td>
                      <td className="py-2 text-right text-gray-600">{bowler.overs ?? '-'}</td>
                      <td className="py-2 text-right text-gray-600">{bowler.maidens ?? 0}</td>
                      <td className="py-2 text-right text-gray-600">{bowler.runs ?? 0}</td>
                      <td className="py-2 text-right font-medium">{bowler.wickets ?? 0}</td>
                      <td className="py-2 text-right text-gray-600">
                        {bowler.economy != null ? bowler.economy.toFixed(2) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      );
    })();

    const theirInningsContent = (() => {
      if (theirStatus === 'NotStarted' || !theirStatus) {
        return (
          <div className="py-6 text-center text-sm text-gray-500">
            {data.opposition ?? 'Opposition'} innings has not yet started.
          </div>
        );
      }

      const theirInningsComplete = theirStatus === 'Completed';

      // Build VCC player ID → name map from all available bowling data
      const vccPlayerMap = new Map<number, string>();
      const addPlayerToMap = (b: OppositionBowlerDetailsV1 | null | undefined) => {
        if (b?.playerId != null && b.playerName) vccPlayerMap.set(b.playerId, b.playerName);
      };
      (data.theirLiveBowlingCard ?? []).forEach(addPlayerToMap);
      addPlayerToMap(data.theirBowlerOneDetails);
      addPlayerToMap(data.theirBowlerTwoDetails);

      const isBBB = data.theirInningsIsBallByBall === true;

      const scoreHeader = (
        <div className="mb-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-700">
          <span className="font-semibold">{data.opposition ?? 'Opposition'}</span>
          {': '}
          <span className="font-semibold text-gray-900">
            {data.theirScore ?? 0}/{data.theirWickets ?? 0}
          </span>
          {isBBB && (data.theirLastCompletedOver ?? 0) > 0 && (
            <span className="text-gray-500"> ({data.theirLastCompletedOver} ov)</span>
          )}
          {!isBBB && (data.theirOver ?? 0) > 0 && (
            <span className="text-gray-500"> ({data.theirOver} ov)</span>
          )}
          {(data.theirRunRate ?? 0) > 0 && (
            <span className="text-gray-500"> · RR {(data.theirRunRate ?? 0).toFixed(2)}</span>
          )}
          {theirStatus === 'InProgress' && (
            <span className="ml-2 text-xs text-green-600 font-semibold">In progress</span>
          )}
        </div>
      );

      if (!isBBB) {
        return (
          <div>
            {scoreHeader}
            {(data.theirCompletedOvers?.length ?? 0) > 0 && (
              <div className="text-xs text-gray-500 mt-2">
                <p className="font-semibold uppercase tracking-wide mb-1">Over summary</p>
                <div className="flex flex-wrap gap-x-2 gap-y-1">
                  {data.theirCompletedOvers!.map((ov, i) => (
                    <span key={i} className="whitespace-nowrap">
                      Ov {ov.over ?? (i + 1)}: {ov.score ?? 0}/{ov.wickets ?? 0}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {(data.theirCompletedOvers?.length ?? 0) === 0 && theirStatus === 'InProgress' && (
              <p className="text-sm text-gray-500">Detailed ball-by-ball data not available for opposition innings.</p>
            )}
          </div>
        );
      }

      // ── Ball-by-ball mode: full opposition innings scorecard ──────────────

      return (
        <div>
          {scoreHeader}

          {/* Change 1 — At the crease */}
          {(data.theirOnStrikeBatsman || data.theirOtherBatsman) && (
            <div className="mb-4 p-3 bg-white border border-gray-200 rounded-lg">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">At the crease</h3>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 border-b border-gray-100">
                    <th className="pb-1.5 text-left font-normal">Batter</th>
                    <th className="pb-1.5 text-right font-normal">R</th>
                    <th className="pb-1.5 text-right font-normal">B</th>
                    <th className="pb-1.5 text-right font-normal">4s</th>
                    <th className="pb-1.5 text-right font-normal">6s</th>
                    <th className="pb-1.5 text-right font-normal">SR</th>
                  </tr>
                </thead>
                <tbody>
                  {data.theirOnStrikeBatsman && (
                    <tr className="border-b border-gray-50">
                      <td className="py-1.5 font-medium">
                        {data.theirOnStrikeBatsman.batsmanName}
                        <span className="ml-1 text-villageGreen text-xs font-bold">*</span>
                      </td>
                      <td className="py-1.5 text-right font-medium">{data.theirOnStrikeBatsman.score ?? 0}</td>
                      <td className="py-1.5 text-right text-gray-600">{data.theirOnStrikeBatsman.ballsFaced ?? 0}</td>
                      <td className="py-1.5 text-right text-gray-600">{data.theirOnStrikeBatsman.fours ?? 0}</td>
                      <td className="py-1.5 text-right text-gray-600">{data.theirOnStrikeBatsman.sixes ?? 0}</td>
                      <td className="py-1.5 text-right text-gray-600">
                        {data.theirOnStrikeBatsman.strikeRate != null
                          ? data.theirOnStrikeBatsman.strikeRate.toFixed(1)
                          : '-'}
                      </td>
                    </tr>
                  )}
                  {data.theirOtherBatsman && (
                    <tr>
                      <td className="py-1.5 font-medium">{data.theirOtherBatsman.batsmanName}</td>
                      <td className="py-1.5 text-right font-medium">{data.theirOtherBatsman.score ?? 0}</td>
                      <td className="py-1.5 text-right text-gray-600">{data.theirOtherBatsman.ballsFaced ?? 0}</td>
                      <td className="py-1.5 text-right text-gray-600">{data.theirOtherBatsman.fours ?? 0}</td>
                      <td className="py-1.5 text-right text-gray-600">{data.theirOtherBatsman.sixes ?? 0}</td>
                      <td className="py-1.5 text-right text-gray-600">
                        {data.theirOtherBatsman.strikeRate != null
                          ? data.theirOtherBatsman.strikeRate.toFixed(1)
                          : '-'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              {data.theirLastBatsmanOut && (
                <p className="mt-2 text-xs text-gray-500 border-t border-gray-100 pt-2">
                  Last wicket:{' '}
                  <span className="font-medium text-gray-700">{data.theirLastBatsmanOut.batsmanName}</span>
                  {' '}{data.theirLastBatsmanOut.score ?? 0} ({data.theirLastBatsmanOut.ballsFaced ?? 0})
                  {' · '}{formatOppositionDismissal(data.theirLastBatsmanOut.wicket, vccPlayerMap, theirInningsComplete)}
                </p>
              )}
            </div>
          )}

          {/* Change 2 — Current partnership */}
          {data.theirCurrentPartnership && (
            <div className="mb-4 p-3 bg-white border border-gray-200 rounded-lg">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Current partnership</h3>
              <p className="text-sm text-gray-800">
                <span className="font-medium">
                  {data.theirCurrentPartnership.batsmanOneName} / {data.theirCurrentPartnership.batsmanTwoName}
                </span>
                {'  '}Runs: <span className="font-semibold">{data.theirCurrentPartnership.score ?? 0}</span>
                {'  '}Balls: {data.theirCurrentPartnership.ballCount ?? 0}
                {'  '}RR: {data.theirCurrentPartnership.runRate != null
                  ? data.theirCurrentPartnership.runRate.toFixed(1) : '-'}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                ({data.theirCurrentPartnership.batsmanOneName} {data.theirCurrentPartnership.batsmanOneScore ?? 0},{' '}
                {data.theirCurrentPartnership.batsmanTwoName} {data.theirCurrentPartnership.batsmanTwoScore ?? 0})
              </p>
              {data.theirPreviousPartnership && (
                <p className="mt-1 text-xs text-gray-400 italic">
                  Previous: {data.theirPreviousPartnership.batsmanOneName} / {data.theirPreviousPartnership.batsmanTwoName}
                  {'  '}{data.theirPreviousPartnership.score ?? 0} runs ({data.theirPreviousPartnership.ballCount ?? 0} balls)
                </p>
              )}
            </div>
          )}

          {/* Change 6 — Bowling panel with current/previous bowler + full card */}
          {(data.theirBowlerOneDetails || (data.theirLiveBowlingCard?.length ?? 0) > 0) && (
            <div className="mb-4 p-3 bg-white border border-gray-200 rounded-lg">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Bowling</h3>
              {(data.theirBowlerOneDetails || data.theirBowlerTwoDetails) && (
                <table className="w-full text-sm mb-3">
                  <tbody>
                    {[data.theirBowlerOneDetails, data.theirBowlerTwoDetails]
                      .filter((b): b is OppositionBowlerDetailsV1 => !!b)
                      .map((b, i) => (
                        <tr key={i} className={i === 0 ? '' : 'border-t border-gray-50'}>
                          <td className="py-1 font-medium">
                            {b.playerName}
                            {i === 0 && <span className="ml-1 text-villageGreen text-sm">►</span>}
                          </td>
                          <td className="py-1 text-right text-gray-600 text-xs tabular-nums">
                            {b.overs ?? 0}-{b.maidens ?? 0}-{b.runs ?? 0}-{b.wickets ?? 0}
                          </td>
                          <td className="py-1 text-right text-gray-500 text-xs">
                            Econ {b.economy != null ? b.economy.toFixed(1) : '-'}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
              {(data.theirLiveBowlingCard?.length ?? 0) > 0 && (
                <details>
                  <summary className="cursor-pointer text-xs text-villageGreen hover:underline select-none">
                    Full bowling card ▼
                  </summary>
                  <table className="w-full text-sm mt-2">
                    <thead>
                      <tr className="border-b border-gray-100 text-xs text-gray-500">
                        <th className="py-1 text-left font-normal">Bowler</th>
                        <th className="py-1 text-right font-normal">O</th>
                        <th className="py-1 text-right font-normal">M</th>
                        <th className="py-1 text-right font-normal">R</th>
                        <th className="py-1 text-right font-normal">W</th>
                        <th className="py-1 text-right font-normal">Econ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.theirLiveBowlingCard!.map((b, i) => (
                        <tr key={i} className="border-b border-gray-50 hover:bg-gray-50">
                          <td className="py-1 font-medium">{b.playerName}</td>
                          <td className="py-1 text-right text-gray-600">{b.overs ?? 0}</td>
                          <td className="py-1 text-right text-gray-600">{b.maidens ?? 0}</td>
                          <td className="py-1 text-right text-gray-600">{b.runs ?? 0}</td>
                          <td className="py-1 text-right font-medium">{b.wickets ?? 0}</td>
                          <td className="py-1 text-right text-gray-600">
                            {b.economy != null ? b.economy.toFixed(2) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </details>
              )}
            </div>
          )}

          {/* Change 3 — Their batting card (collapsible) */}
          {(data.theirLiveBattingCard?.length ?? 0) > 0 && (
            <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                className="w-full flex justify-between items-center px-3 py-2 text-left bg-white"
                onClick={() => setTheirBattingExpanded(p => !p)}
                aria-expanded={theirBattingExpanded}
              >
                <span className="text-sm font-semibold text-gray-700">Their batting card</span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${theirBattingExpanded ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {theirBattingExpanded && (
                <div className="border-t border-gray-100 px-3 py-3">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2 border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wide">
                          <th className="py-2 pr-1 text-center">#</th>
                          <th className="py-2 pr-2">Batter</th>
                          <th className="py-2 text-gray-400 font-normal hidden sm:table-cell">Dismissal</th>
                          <th className="py-2 text-right">R</th>
                          <th className="py-2 text-right">B</th>
                          <th className="py-2 text-right">4s</th>
                          <th className="py-2 text-right">6s</th>
                          <th className="py-2 text-right">SR</th>
                        </tr>
                      </thead>
                      <tbody className="text-gray-800">
                        {data.theirLiveBattingCard!.map((entry, idx) => {
                          const dismissalText = formatOppositionDismissal(
                            entry.wicket, vccPlayerMap, theirInningsComplete
                          );
                          return (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-2 pr-1 text-center text-gray-400 text-xs">{idx + 1}</td>
                              <td className="py-2 pr-2 font-medium">
                                {entry.batsmanName}
                                <span className="block text-xs text-gray-400 font-normal sm:hidden mt-0.5">
                                  {dismissalText}
                                </span>
                              </td>
                              <td className="py-2 text-sm text-gray-500 hidden sm:table-cell">{dismissalText}</td>
                              <td className="py-2 text-right font-medium">{entry.score ?? 0}</td>
                              <td className="py-2 text-right text-gray-600">{entry.ballsFaced ?? 0}</td>
                              <td className="py-2 text-right text-gray-600">{entry.fours ?? 0}</td>
                              <td className="py-2 text-right text-gray-600">{entry.sixes ?? 0}</td>
                              <td className="py-2 text-right text-gray-600">
                                {entry.strikeRate != null
                                  ? entry.strikeRate.toFixed(1)
                                  : (entry.ballsFaced
                                    ? ((entry.score ?? 0) / entry.ballsFaced * 100).toFixed(1)
                                    : '-')}
                              </td>
                            </tr>
                          );
                        })}
                        {(data.theirYetToBat?.length ?? 0) > 0 && (
                          <>
                            <tr className="border-t border-gray-200">
                              <td colSpan={8} className="pt-2 pb-1 text-xs font-semibold text-gray-400 uppercase tracking-wide">
                                Yet to bat
                              </td>
                            </tr>
                            {data.theirYetToBat!.map((entry, idx) => (
                              <tr key={`ytb-${idx}`} className="border-b border-gray-50">
                                <td colSpan={8} className="py-1 text-sm text-gray-500">{entry.batsmanName}</td>
                              </tr>
                            ))}
                          </>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Change 4 — Fall of wickets (collapsible) */}
          {(data.theirFallOfWickets?.length ?? 0) > 0 && (
            <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                className="w-full flex justify-between items-center px-3 py-2 text-left bg-white"
                onClick={() => setTheirFowExpanded(p => !p)}
                aria-expanded={theirFowExpanded}
              >
                <span className="text-sm font-semibold text-gray-700">Fall of wickets</span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${theirFowExpanded ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {theirFowExpanded && (
                <div className="border-t border-gray-100 px-3 py-3">
                  <div className="space-y-1.5">
                    {data.theirFallOfWickets!.map((fow, i) => (
                      <div key={i} className="text-sm text-gray-700 flex flex-wrap gap-x-2">
                        <span className="font-mono font-semibold whitespace-nowrap">
                          {fow.wicketNumber}-{fow.teamScore}
                        </span>
                        <span className="text-gray-500 whitespace-nowrap">
                          ({fow.outgoingBatsmanName} {fow.outgoingBatsmanScore}, {fow.overAsString} ov)
                        </span>
                        <span className="text-gray-600">
                          {formatOppositionDismissal(fow.wicket, vccPlayerMap, true)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Change 5 — Over by over (collapsible) */}
          {(data.theirBallByBallCompletedOvers?.length ?? 0) > 0 && (
            <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                className="w-full flex justify-between items-center px-3 py-2 text-left bg-white"
                onClick={() => setTheirOverByOverExpanded(p => !p)}
                aria-expanded={theirOverByOverExpanded}
              >
                <span className="text-sm font-semibold text-gray-700">Over by over</span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${theirOverByOverExpanded ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {theirOverByOverExpanded && (
                <div className="border-t border-gray-100 px-3 py-3">
                  <div className="space-y-3">
                    {data.theirBallByBallCompletedOvers!.map((overSummary, i) => {
                      const overNum = overSummary.over?.overNumber ?? (i + 1);
                      const runsThisOver = overSummary.scoreForThisOver ?? 0;
                      const prevWickets = i === 0
                        ? 0
                        : (data.theirBallByBallCompletedOvers![i - 1].wicketsAtEndOfOver ?? 0);
                      const wicketsThisOver = (overSummary.wicketsAtEndOfOver ?? 0) - prevWickets;
                      const balls = overSummary.over?.balls ?? [];
                      return (
                        <div key={i} className="text-sm">
                          <div className="flex items-start gap-3">
                            <span className="text-xs font-semibold text-gray-500 w-10 flex-shrink-0 pt-1">
                              Ov {overNum}
                            </span>
                            <div className="flex-1">
                              <div className="flex flex-wrap items-center gap-1 mb-1">
                                {balls.map((ball, bi) => {
                                  const { label, className: blobCls } = getOppositionBallBlob(ball);
                                  return (
                                    <span
                                      key={bi}
                                      className={`inline-flex items-center justify-center w-6 h-6 rounded-full font-bold flex-shrink-0 ${label.length >= 3 ? 'text-[9px]' : 'text-xs'} ${blobCls}`}
                                    >
                                      {label}
                                    </span>
                                  );
                                })}
                                <span className="text-xs text-gray-500 ml-1">
                                  {runsThisOver} run{runsThisOver !== 1 ? 's' : ''}
                                  {wicketsThisOver > 0 && ` · ${wicketsThisOver} wkt${wicketsThisOver !== 1 ? 's' : ''}`}
                                  {' '}(Total: {overSummary.scoreAtEndOfOver ?? 0}-{overSummary.wicketsAtEndOfOver ?? 0})
                                </span>
                              </div>
                              {overSummary.over?.commentary && (
                                <p className="text-xs text-gray-400 italic">{overSummary.over.commentary}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Change 7 — Partnerships (collapsible) */}
          {(data.theirPartnerships?.length ?? 0) > 0 && (
            <div className="mb-3 border border-gray-200 rounded-lg overflow-hidden">
              <button
                type="button"
                className="w-full flex justify-between items-center px-3 py-2 text-left bg-white"
                onClick={() => setTheirPartnershipsExpanded(p => !p)}
                aria-expanded={theirPartnershipsExpanded}
              >
                <span className="text-sm font-semibold text-gray-700">Partnerships</span>
                <svg
                  className={`w-4 h-4 text-gray-400 transition-transform ${theirPartnershipsExpanded ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {theirPartnershipsExpanded && (
                <div className="border-t border-gray-100 px-3 py-3">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200 text-xs text-gray-500">
                          <th className="py-1.5 text-left font-normal">#</th>
                          <th className="py-1.5 text-left font-normal">Batters</th>
                          <th className="py-1.5 text-right font-normal">Runs</th>
                          <th className="py-1.5 text-right font-normal">Balls</th>
                          <th className="py-1.5 text-right font-normal">RR</th>
                          <th className="py-1.5 text-right font-normal">Ended at</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.theirPartnerships!.map((p, i) => {
                          const isOngoing = i === data.theirPartnerships!.length - 1
                            && theirStatus === 'InProgress';
                          const fowEntry = data.theirFallOfWickets?.[i];
                          return (
                            <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="py-1.5 text-gray-400 text-xs">{toOrdinal(i + 1)}</td>
                              <td className="py-1.5 font-medium">
                                {p.batsmanOneName} / {p.batsmanTwoName}
                                {isOngoing && (
                                  <span className="ml-1.5 text-xs bg-green-100 text-green-700 px-1 py-0.5 rounded font-semibold">
                                    ongoing
                                  </span>
                                )}
                              </td>
                              <td className="py-1.5 text-right">{p.score ?? 0}</td>
                              <td className="py-1.5 text-right text-gray-600">{p.ballCount ?? 0}</td>
                              <td className="py-1.5 text-right text-gray-600">
                                {p.runRate != null ? p.runRate.toFixed(1) : '-'}
                              </td>
                              <td className="py-1.5 text-right text-gray-500 text-xs">
                                {isOngoing
                                  ? '—'
                                  : fowEntry
                                    ? `${fowEntry.wicketNumber}-${fowEntry.teamScore}`
                                    : '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      );
    })();

    const showBothInnings = !!(ourStatus && ourStatus !== 'NotStarted') && !!(theirStatus && theirStatus !== 'NotStarted');

    return (
      <section className="max-w-6xl mx-auto mt-4">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <button
            type="button"
            className="w-full flex justify-between items-center px-4 py-3 text-left"
            onClick={() => setScorecardExpanded(prev => !prev)}
            aria-expanded={scorecardExpanded}
          >
            <span className="text-sm font-semibold text-gray-700">Scorecards</span>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${scorecardExpanded ? 'rotate-180' : ''}`}
              fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {scorecardExpanded && (
            <div className="border-t border-gray-100 px-4 py-4">
              {showBothInnings && (
                <div className="flex gap-2 mb-4 flex-wrap">
                  <button
                    type="button"
                    onClick={() => setActiveInnings('our')}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${
                      activeInnings === 'our'
                        ? 'bg-villageGreen text-white'
                        : 'border border-villageGreen text-villageGreen hover:bg-villageGreenLight'
                    }`}
                  >
                    The Village CC Innings
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveInnings('their')}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${
                      activeInnings === 'their'
                        ? 'bg-villageGreen text-white'
                        : 'border border-villageGreen text-villageGreen hover:bg-villageGreenLight'
                    }`}
                  >
                    {data.opposition ?? 'Opposition'} Innings
                  </button>
                </div>
              )}
              {(!showBothInnings || activeInnings === 'our') && ourInningsContent}
              {(!showBothInnings || activeInnings === 'their') && theirInningsContent}
            </div>
          )}
        </div>
      </section>
    );
  })();

  const matchReportSection = (completed && scorecardData.matchReport && (scorecardData.matchReport.conditions || scorecardData.matchReport.report)) ? (
    <section className="max-w-6xl mx-auto mt-6">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-6 py-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4">Match Report</h2>
        {scorecardData.matchReport.conditions && (
          <div className="mb-4">
            <h3 className="font-semibold text-gray-900 mb-2">Conditions</h3>
            <p className="text-sm text-gray-700">{scorecardData.matchReport.conditions}</p>
          </div>
        )}
        {scorecardData.matchReport.report && (
          <div>
            <h3 className="font-semibold text-gray-900 mb-2">Report</h3>
            <div className="text-sm text-gray-700 prose prose-sm max-w-none"
                 dangerouslySetInnerHTML={{ __html: scorecardData.matchReport.report }} />
          </div>
        )}
      </div>
    </section>
  ) : null;

  // ── CRR / RRR computations (hoisted – used in both hero and At the Crease) ──
  const isLimitedOversMatch = !data.declarationGame && (data.overs ?? 0) > 0;
  const ourInningsActive = data.ourInningsStatus === 'InProgress';
  const theirInningsActive = data.theirInningsStatus === 'InProgress';
  const ourInningsDone  = data.ourInningsStatus === 'Completed';
  const theirInningsDone = data.theirInningsStatus === 'Completed';

  // We're chasing (batting second, their innings finished)
  const weAreChasing  = isLimitedOversMatch && ourInningsActive  && theirInningsDone;
  // They're chasing (bowling second, our innings finished)
  const theyAreChasing = isLimitedOversMatch && theirInningsActive && ourInningsDone;

  const ourTarget        = weAreChasing  ? (data.theirScore ?? 0) + 1 : null;
  const ourRunsNeeded    = weAreChasing  && ourTarget !== null  ? ourTarget  - (data.score ?? 0) : null;
  const ourOversRemaining = weAreChasing
    ? (data.oversRemaining ?? Math.max(0, (data.overs ?? 0) - (data.ourLastCompletedOver ?? 0)))
    : null;
  const ourRRR = weAreChasing && ourOversRemaining != null && ourOversRemaining > 0 && ourRunsNeeded != null
    ? Math.round(ourRunsNeeded / ourOversRemaining * 100) / 100
    : null;

  const theirTarget        = theyAreChasing ? (data.score ?? 0) + 1 : null;
  const theirRunsNeeded    = theyAreChasing && theirTarget !== null ? theirTarget - (data.theirScore ?? 0) : null;
  const theirOversRemaining = theyAreChasing && (data.overs ?? 0) > 0
    ? Math.max(0, (data.overs ?? 0) - (data.theirOver ?? 0))
    : null;
  const theirRRR = theyAreChasing && theirOversRemaining != null && theirOversRemaining > 0 && theirRunsNeeded != null
    ? Math.round(theirRunsNeeded / theirOversRemaining * 100) / 100
    : null;
  // ─────────────────────────────────────────────────────────────────────────

  // At the Crease section for live matches — always visible, first section after hero
  const atTheCreaseSection = (() => {
    if (!live) return null;

    const completedOversForDisplay = data.completedOvers ?? [];
    const hasAtCrease = !!(data.onStrikeBatsman || data.otherBatsman || data.bowlerOneDetails || data.bowlerTwoDetails);
    const hasOversDisplay = completedOversForDisplay.length > 0;

    if (!hasAtCrease && !hasOversDisplay) return null;

    const recentOvers = completedOversForDisplay.slice(-5);

    return (
      <section className="max-w-6xl mx-auto mt-2 sm:mt-6">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm px-4 sm:px-6 py-4 sm:py-6">
          <h2 className="text-base font-semibold text-gray-800 mb-3 sm:mb-4">At the Crease</h2>

          {hasAtCrease && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-3 sm:mb-4">
              {(data.onStrikeBatsman || data.otherBatsman) && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Batting</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 border-b border-gray-100">
                        <th className="pb-1.5 text-left font-normal">Batter</th>
                        <th className="pb-1.5 text-right font-normal">R</th>
                        <th className="pb-1.5 text-right font-normal">B</th>
                        <th className="pb-1.5 text-right font-normal">4s</th>
                        <th className="pb-1.5 text-right font-normal">6s</th>
                        <th className="pb-1.5 text-right font-normal">SR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.onStrikeBatsman && (() => {
                        const pid = data.onStrikeBatsman.playerId;
                        const isExpandable = !!pid;
                        const isExpanded = isExpandable && expandedBatterIds.has(pid!);
                        const isLoadingProfile = isExpandable && loadingBatterIds.has(pid!);
                        const profile = isExpandable ? batterProfiles.get(pid!) : undefined;
                        return (
                          <React.Fragment>
                            <tr
                              className={`border-b border-gray-50 ${isExpandable ? 'cursor-pointer select-none' : ''}`}
                              onClick={isExpandable ? () => toggleBatterProfile(pid!) : undefined}
                            >
                              <td className="py-1.5 font-medium">
                                <span className="inline-flex items-center gap-0.5">
                                  {data.onStrikeBatsman!.name}
                                  <span className="ml-1 text-villageGreen text-xs font-bold">*</span>
                                  {isExpandable && <ChevronDownIcon expanded={isExpanded} />}
                                </span>
                              </td>
                              <td className="py-1.5 text-right font-medium">{data.onStrikeBatsman!.score ?? 0}</td>
                              <td className="py-1.5 text-right text-gray-600">{data.onStrikeBatsman!.balls ?? 0}</td>
                              <td className="py-1.5 text-right text-gray-600">{data.onStrikeBatsman!.fours ?? 0}</td>
                              <td className="py-1.5 text-right text-gray-600">{data.onStrikeBatsman!.sixes ?? 0}</td>
                              <td className="py-1.5 text-right text-gray-600">
                                {data.onStrikeBatsman!.strikeRate != null ? data.onStrikeBatsman!.strikeRate.toFixed(1) : '-'}
                              </td>
                            </tr>
                            {isExpanded && isLoadingProfile && <LoadingProfileRow colSpan={6} />}
                            {isExpanded && !isLoadingProfile && profile && <BatterMiniProfile profile={profile} colSpan={6} />}
                          </React.Fragment>
                        );
                      })()}
                      {data.otherBatsman && (() => {
                        const pid = data.otherBatsman.playerId;
                        const isExpandable = !!pid;
                        const isExpanded = isExpandable && expandedBatterIds.has(pid!);
                        const isLoadingProfile = isExpandable && loadingBatterIds.has(pid!);
                        const profile = isExpandable ? batterProfiles.get(pid!) : undefined;
                        return (
                          <React.Fragment>
                            <tr
                              className={isExpandable ? 'cursor-pointer select-none' : ''}
                              onClick={isExpandable ? () => toggleBatterProfile(pid!) : undefined}
                            >
                              <td className="py-1.5 font-medium">
                                <span className="inline-flex items-center gap-0.5">
                                  {data.otherBatsman!.name}
                                  {isExpandable && <ChevronDownIcon expanded={isExpanded} />}
                                </span>
                              </td>
                              <td className="py-1.5 text-right font-medium">{data.otherBatsman!.score ?? 0}</td>
                              <td className="py-1.5 text-right text-gray-600">{data.otherBatsman!.balls ?? 0}</td>
                              <td className="py-1.5 text-right text-gray-600">{data.otherBatsman!.fours ?? 0}</td>
                              <td className="py-1.5 text-right text-gray-600">{data.otherBatsman!.sixes ?? 0}</td>
                              <td className="py-1.5 text-right text-gray-600">
                                {data.otherBatsman!.strikeRate != null ? data.otherBatsman!.strikeRate.toFixed(1) : '-'}
                              </td>
                            </tr>
                            {isExpanded && isLoadingProfile && <LoadingProfileRow colSpan={6} />}
                            {isExpanded && !isLoadingProfile && profile && <BatterMiniProfile profile={profile} colSpan={6} />}
                          </React.Fragment>
                        );
                      })()}
                    </tbody>
                  </table>
                  {data.currentPartnership && (
                    <p className="mt-2 text-xs text-gray-500">
                      Partnership: {data.currentPartnership.score ?? 0} runs
                      {data.currentPartnership.oversAsString && ` (${data.currentPartnership.oversAsString} ov)`}
                    </p>
                  )}
                  {(data.yetToBat?.length ?? 0) > 0 && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Yet to bat</p>
                      <p className="text-xs text-gray-600">
                        {data.yetToBat!.map((e: YetToBatEntryV1) => e.playerName).join(', ')}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {(data.bowlerOneDetails || data.bowlerTwoDetails) && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Bowling</h3>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-gray-500 border-b border-gray-100">
                        <th className="pb-1.5 text-left font-normal">Bowler</th>
                        <th className="pb-1.5 text-right font-normal">O</th>
                        <th className="pb-1.5 text-right font-normal">M</th>
                        <th className="pb-1.5 text-right font-normal">R</th>
                        <th className="pb-1.5 text-right font-normal">W</th>
                        <th className="pb-1.5 text-right font-normal">Econ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[data.bowlerOneDetails, data.bowlerTwoDetails].filter(Boolean).map((bowler, i, arr) => (
                        <tr key={i} className={`${i < arr.length - 1 ? 'border-b border-gray-50' : ''} ${flashKeys.has(i === 0 ? 'bowler-1' : 'bowler-2') ? 'live-flash' : ''}`}>
                          <td className="py-1.5 font-medium">{bowler!.name}</td>
                          <td className="py-1.5 text-right text-gray-600">{bowler!.details?.overs ?? '-'}</td>
                          <td className="py-1.5 text-right text-gray-600">{bowler!.details?.maidens ?? 0}</td>
                          <td className="py-1.5 text-right text-gray-600">{bowler!.details?.runs ?? 0}</td>
                          <td className="py-1.5 text-right font-medium">{bowler!.details?.wickets ?? 0}</td>
                          <td className="py-1.5 text-right text-gray-600">
                            {bowler!.details?.economy != null ? bowler!.details.economy.toFixed(2) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Score bar with CRR / RRR */}
          <div className={`bg-gray-50 rounded-lg px-4 py-3 mb-4 ${flashKeys.has('our-score') || flashKeys.has('their-score') || flashKeys.has('new-over') ? 'live-flash' : ''}`}>
            {/* Score headline */}
            <div className="text-sm font-semibold text-gray-900 mb-2">
              {ourInningsActive ? (
                <>The Village CC{' '}
                  <span className="text-villageGreen">{data.score}/{data.wickets}</span>
                  {' '}({data.ourLastCompletedOver}{isLimitedOversMatch && data.overs ? `/${data.overs}` : ''} ov)
                </>
              ) : theirInningsActive ? (
                <>{data.opposition}{' '}
                  <span className="text-villageGreen">{data.theirScore}/{data.theirWickets}</span>
                  {' '}({data.theirOver}{isLimitedOversMatch && data.overs ? `/${data.overs}` : ''} ov)
                </>
              ) : null}
            </div>
            {/* Stat chips: CRR · RRR · Need X off Y · Target */}
            <div className="flex flex-wrap gap-x-5 gap-y-2 items-end">
              {/* CRR */}
              {(ourInningsActive || theirInningsActive) && (
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">CRR</span>
                  <span className="text-xl font-bold text-gray-900">
                    {ourInningsActive ? (data.runRate ?? 0).toFixed(2) : (data.theirRunRate ?? 0).toFixed(2)}
                  </span>
                </div>
              )}
              {/* RRR */}
              {(ourRRR !== null || theirRRR !== null) && (() => {
                const rrr = ourRRR ?? theirRRR!;
                const crr = ourRRR !== null ? (data.runRate ?? 0) : (data.theirRunRate ?? 0);
                const color = rrr <= crr ? 'text-green-700' : 'text-red-600';
                return (
                  <div className="flex flex-col leading-tight">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">RRR</span>
                    <span className={`text-xl font-bold ${color}`}>{rrr.toFixed(2)}</span>
                  </div>
                );
              })()}
              {/* "Need X off Y ov" – our chase */}
              {weAreChasing && ourRunsNeeded !== null && ourOversRemaining !== null && ourOversRemaining > 0 && (
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Need</span>
                  <span className="text-xl font-bold text-gray-900">
                    {ourRunsNeeded} off {ourOversRemaining} ov
                  </span>
                </div>
              )}
              {/* "They need X off Y ov" – their chase */}
              {theyAreChasing && theirRunsNeeded !== null && theirOversRemaining !== null && theirOversRemaining > 0 && (
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">They need</span>
                  <span className="text-xl font-bold text-gray-900">
                    {theirRunsNeeded} off {theirOversRemaining} ov
                  </span>
                </div>
              )}
              {/* Target */}
              {ourTarget !== null && (
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Target</span>
                  <span className="text-xl font-bold text-gray-900">{ourTarget}</span>
                </div>
              )}
              {theirTarget !== null && (
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Target</span>
                  <span className="text-xl font-bold text-gray-900">{theirTarget}</span>
                </div>
              )}
              {/* Overs remaining (first innings, no target yet) */}
              {ourInningsActive && !weAreChasing && isLimitedOversMatch && ourOversRemaining == null && data.overs && (
                <div className="flex flex-col leading-tight">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Ov left</span>
                  <span className="text-xl font-bold text-gray-900">
                    {Math.max(0, (data.overs ?? 0) - (data.ourLastCompletedOver ?? 0))}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Horizontal over-by-over ball display */}
          {hasOversDisplay && (
            <div className="overflow-x-auto">
              <div
                className={`flex items-center gap-0 text-sm font-mono whitespace-nowrap py-1 ${flashKeys.has('new-over') ? 'live-flash' : ''}`}
                data-testid="horizontal-overs"
              >
                {[...recentOvers].reverse().map((overData, idx) => {
                  const overNum = overData.over?.overNumber ?? (completedOversForDisplay.length - idx);
                  const balls = overData.over?.balls
                    ? [...overData.over.balls].sort((a, b) => (b.ballNumber ?? 0) - (a.ballNumber ?? 0))
                    : [];
                  const runsThisOver = overData.scoreForThisOver;
                  return (
                    <span key={idx} className="flex items-center flex-shrink-0">
                      {idx > 0 && <span className="mx-2 text-gray-400">|</span>}
                      {runsThisOver !== undefined && runsThisOver !== null && (
                        <span className="text-xs font-semibold text-gray-500 flex-shrink-0 mx-1">
                          ({runsThisOver})
                        </span>
                      )}
                      {balls.length > 0 ? balls.map((ball, bi) => {
                        const blob = getBallBlob(ball);
                        return (
                          <span
                            key={bi}
                            data-testid="ball-blob"
                            className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${blob.label.length >= 3 ? 'text-[9px]' : 'text-xs'} font-bold flex-shrink-0 mx-0.5 ${blob.className}`}
                          >
                            {blob.label}
                          </span>
                        );
                      }) : <span className="text-gray-400">—</span>}
                      <span className="font-semibold text-gray-700 ml-1">{toOrdinal(overNum)}</span>
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>
    );
  })();

  return (
    <div className="font-sans text-villageText bg-gray-50 min-h-screen">
      <Header />

      <main>
        {/* Hero Match Card */}
        <section className="bg-white border border-gray-200 rounded-xl shadow-sm max-w-6xl mx-auto mt-2 sm:mt-6 px-4 sm:px-6 py-3 sm:py-5">
          {/* Match meta */}
          <p className="text-center text-xs text-gray-500 mb-2 sm:mb-4">
            {scorecardData.matchData?.type || (data.declarationGame ? 'Declaration' : `${data.overs}-over match`)}
            {' · '}
            {scorecardData.result?.venueName || scorecardData.matchData?.venue?.name || 'TBC'}
            {' · '}
            {formatDate(scorecardData.matchData?.date || scorecardData.result?.matchDate || undefined)}
          </p>

          <div className="flex flex-row items-center justify-between gap-2 sm:gap-6">
            {/* Left team */}
            {oppositionIsHomeTeam ? (
              <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-0 flex-1 text-center">
                {oppositionIcon('h-9 w-9 sm:h-14 sm:w-14')}
                <h1 className="text-xs sm:text-base font-semibold">{data.opposition}</h1>
                {theirScoreDisplay && <p className="text-xl sm:text-3xl font-bold">{theirScoreDisplay}</p>}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-0 flex-1 text-center">
                {villageIcon('h-9 w-9 sm:h-14 sm:w-14')}
                <h1 className="text-xs sm:text-base font-semibold">The Village CC</h1>
                {ourScoreDisplay && <p className="text-xl sm:text-3xl font-bold">{ourScoreDisplay}</p>}
              </div>
            )}

            {/* Centre: Status */}
            <div className="flex flex-col items-center gap-1 flex-shrink-0 px-1 text-center">
              {live ? (
                <>
                  <span className="bg-green-600 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                    LIVE
                  </span>
                  <span className="h-2 w-2 bg-green-600 rounded-full animate-pulse"></span>
                  {lastUpdated && (
                    <span className="text-[10px] text-gray-400 whitespace-nowrap">
                      {lastUpdated.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                    </span>
                  )}
                </>
              ) : completed ? (
                <>
                  <span className="text-xs sm:text-sm font-semibold text-gray-700 whitespace-nowrap">
                    {scorecardData.result?.resultText?.trim() || 'COMPLETED'}
                  </span>
                  {!scorecardData.result?.isAbandoned &&
                    scorecardData.result?.margin &&
                    scorecardData.result.margin.trim() !== '' &&
                    scorecardData.result.margin !== 'result not yet in' && (
                    <span className="text-xs text-gray-500 whitespace-nowrap">{scorecardData.result.margin}</span>
                  )}
                </>
              ) : scorecardData.result?.isAbandoned ? (
                <span className="bg-yellow-600 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                  ABANDONED
                </span>
              ) : (
                <span className="bg-blue-600 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                  SCHEDULED
                </span>
              )}
            </div>

            {/* Right team */}
            {oppositionIsHomeTeam ? (
              <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-0 flex-1 text-center">
                {villageIcon('h-9 w-9 sm:h-14 sm:w-14')}
                <h1 className="text-xs sm:text-base font-semibold">The Village CC</h1>
                {ourScoreDisplay && <p className="text-xl sm:text-3xl font-bold">{ourScoreDisplay}</p>}
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1 sm:gap-2 min-w-0 flex-1 text-center">
                {oppositionIcon('h-9 w-9 sm:h-14 sm:w-14')}
                <h1 className="text-xs sm:text-base font-semibold">{data.opposition}</h1>
                {theirScoreDisplay && <p className="text-xl sm:text-3xl font-bold">{theirScoreDisplay}</p>}
              </div>
            )}
          </div>

          {/* Toss info */}
          <p className="mt-2 sm:mt-3 text-center text-xs text-gray-500">
            {data.wonToss ? 'The Village CC' : data.opposition} won the toss and elected to {data.tossWinnerBatted ? 'bat' : 'field'}
          </p>

          {/* CRR / RRR hero strip — live limited-overs only */}
          {live && isLimitedOversMatch && (ourInningsActive || theirInningsActive) && (
            <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap justify-center gap-x-6 gap-y-2 items-end">
              {/* CRR */}
              <div className="flex flex-col items-center leading-tight">
                <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">CRR</span>
                <span className="text-2xl font-bold text-gray-900">
                  {ourInningsActive ? (data.runRate ?? 0).toFixed(2) : (data.theirRunRate ?? 0).toFixed(2)}
                </span>
              </div>
              {/* RRR */}
              {(ourRRR !== null || theirRRR !== null) && (() => {
                const rrr = ourRRR ?? theirRRR!;
                const crr = ourRRR !== null ? (data.runRate ?? 0) : (data.theirRunRate ?? 0);
                const color = rrr <= crr ? 'text-green-700' : 'text-red-600';
                return (
                  <div className="flex flex-col items-center leading-tight">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">RRR</span>
                    <span className={`text-2xl font-bold ${color}`}>{rrr.toFixed(2)}</span>
                  </div>
                );
              })()}
              {/* Need X off Y ov — our chase */}
              {weAreChasing && ourRunsNeeded !== null && ourOversRemaining !== null && ourOversRemaining > 0 && (
                <div className="flex flex-col items-center leading-tight">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Need</span>
                  <span className="text-2xl font-bold text-gray-900">{ourRunsNeeded} off {ourOversRemaining} ov</span>
                </div>
              )}
              {/* They need X off Y ov — their chase */}
              {theyAreChasing && theirRunsNeeded !== null && theirOversRemaining !== null && theirOversRemaining > 0 && (
                <div className="flex flex-col items-center leading-tight">
                  <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">They need</span>
                  <span className="text-2xl font-bold text-gray-900">{theirRunsNeeded} off {theirOversRemaining} ov</span>
                </div>
              )}
              {/* Overs remaining — first innings */}
              {!weAreChasing && !theyAreChasing && isLimitedOversMatch && (() => {
                const oversUsed = ourInningsActive ? (data.ourLastCompletedOver ?? 0) : (data.theirOver ?? 0);
                const totalOvers = data.overs ?? 0;
                const remaining = Math.max(0, totalOvers - oversUsed);
                if (remaining <= 0) return null;
                return (
                  <div className="flex flex-col items-center leading-tight">
                    <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Overs left</span>
                    <span className="text-2xl font-bold text-gray-900">{remaining}</span>
                  </div>
                );
              })()}
            </div>
          )}
        </section>

        {/* Sections: same collapsible pattern for both live and completed */}
        {matchReportSection}
        {atTheCreaseSection}
        {scorecardSection}
        {commentarySection}
        {/* Team Analysis section */}
            {((data.completedOvers?.length ?? 0) > 0 || (data.partnerships?.length ?? 0) > 0) && (() => {
              const ourOvers = data.completedOvers ?? [];
              const theirOvers = data.theirCompletedOvers ?? [];
              const partnerships = data.partnerships ?? [];

              const ourCumulative = ourOvers.map(o => o.scoreAtEndOfOver ?? 0);
              const ourPerOver = ourOvers.map(o => o.scoreForThisOver ?? 0);

              const theirByOver: number[] = [];
              const theirCumulative: number[] = [];
              if (theirOvers.length > 0) {
                const sortedTheirOvers = [...theirOvers]
                  .filter(o => (o.over ?? 0) > 0)
                  .sort((a, b) => (a.over ?? 0) - (b.over ?? 0));
                let prevOver = 0;
                let prevScore = 0;
                for (const point of sortedTheirOvers) {
                  const currentOver = point.over ?? 0;
                  const currentScore = point.score ?? prevScore;
                  const runsInSegment = currentScore - prevScore;
                  const oversInSegment = currentOver - prevOver;
                  const runsPerOver = oversInSegment > 0 ? runsInSegment / oversInSegment : 0;
                  for (let ov = prevOver + 1; ov <= currentOver; ov++) {
                    theirByOver.push(runsPerOver);
                    theirCumulative.push(prevScore + runsPerOver * (ov - prevOver));
                  }
                  prevOver = currentOver;
                  prevScore = currentScore;
                }
              }

              const wagonWheelBalls = ourOvers
                .flatMap(o => o.over?.balls ?? [])
                .filter(b => b.angle != null && (b.thing === '' || b.thing === null || b.thing === undefined || (b.thing === 'nb' && (b.amount ?? 0) > 1)));

              const tabBtn = (tab: 'worm' | 'manhattan' | 'partnerships' | 'wagon', label: string) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setActiveAnalysisTab(tab)}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    activeAnalysisTab === tab
                      ? 'bg-villageGreen text-white'
                      : 'border border-villageGreen text-villageGreen hover:bg-villageGreenLight'
                  }`}
                >
                  {label}
                </button>
              );

              const wormData = {
                labels: Array.from(
                  { length: Math.max(ourCumulative.length, theirCumulative.length) },
                  (_, i) => String(i + 1)
                ),
                datasets: [
                  {
                    label: 'The Village CC',
                    data: ourCumulative,
                    borderColor: '#1d7a4b',
                    backgroundColor: 'transparent',
                    tension: 0.1,
                    pointRadius: 2,
                  },
                  ...(theirCumulative.length > 0 ? [{
                    label: data.opposition ?? 'Opposition',
                    data: theirCumulative,
                    borderColor: '#d4a017',
                    backgroundColor: 'transparent',
                    tension: 0.1,
                    pointRadius: 2,
                  }] : []),
                ],
              };

              const allManhattanLabels = (() => {
                const maxLen = Math.max(ourPerOver.length, theirByOver.length);
                return Array.from({ length: maxLen }, (_, i) => String(i + 1));
              })();

              const manhattanData = {
                labels: allManhattanLabels,
                datasets: [
                  {
                    label: 'The Village CC',
                    data: ourPerOver,
                    backgroundColor: '#1d7a4b',
                  },
                  ...(theirByOver.length > 0 ? [{
                    label: data.opposition ?? 'Opposition',
                    data: theirByOver,
                    backgroundColor: '#d4a017',
                  }] : []),
                ],
              };

              const chartOptions = {
                responsive: true,
                plugins: { legend: { position: 'top' as const } },
              };

              return (
                <section className="max-w-6xl mx-auto mt-4">
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex justify-between items-center px-4 py-3 text-left"
                    onClick={() => setAnalysisExpanded(prev => !prev)}
                    aria-expanded={analysisExpanded}
                  >
                    <span className="text-sm font-semibold text-gray-700">Team Analysis</span>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${analysisExpanded ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {analysisExpanded && (
                    <div className="border-t border-gray-100 px-4 py-4">
                      <div className="flex gap-2 mb-3 flex-wrap">
                        {ourOvers.length > 0 && tabBtn('worm', 'Worm')}
                        {ourOvers.length > 0 && tabBtn('manhattan', 'Manhattan')}
                        {partnerships.length > 0 && tabBtn('partnerships', 'Partnerships')}
                        {wagonWheelBalls.length > 0 && tabBtn('wagon', 'Wagon Wheel')}
                      </div>
                    {activeAnalysisTab === 'worm' && ourOvers.length > 0 && (
                      <Line data={wormData} options={chartOptions} />
                    )}
                    {activeAnalysisTab === 'manhattan' && ourOvers.length > 0 && (
                      <Bar data={manhattanData} options={chartOptions} />
                    )}
                    {activeAnalysisTab === 'partnerships' && partnerships.length > 0 && (() => {
                      const svgW = 600;
                      const rowH = 40;
                      const rowGap = 8;
                      const centerX = svgW / 2;
                      const halfW = centerX - 4;
                      const highScore = Math.max(
                        ...partnerships.map(p => Math.max(p.player1Score ?? 0, p.player2Score ?? 0)),
                        1
                      );
                      const svgH = partnerships.length * (rowH + rowGap) + rowGap + 20;
                      const barSize = (score: number) => (score / Math.max(highScore, 1)) * halfW;
                      const textPad = 6;
                      const minBarWidthForInsideLabel = 60;
                      // Build name lookup: prefer finalScorecard entries, fall back to ball-by-ball data
                      const battingEntries = scorecardData.finalScorecard?.ourInnings?.batting?.entries ?? [];
                      const ballPlayerNames = new Map<number, string>();
                      (data.completedOvers ?? []).forEach(o => {
                        o.over?.balls?.forEach(b => {
                          if (b.batsman != null && b.batsmanName) ballPlayerNames.set(b.batsman, b.batsmanName);
                        });
                      });
                      const getPlayerNameById = (id?: number) => {
                        if (id == null) return undefined;
                        return battingEntries.find((e: BattingEntryV1) => e.playerId === id)?.playerName
                          ?? ballPlayerNames.get(id)
                          ?? undefined;
                      };
                      return (
                        <svg
                          data-testid="partnerships-chart"
                          viewBox={`0 0 ${svgW} ${svgH}`}
                          className="w-full"
                          style={{ maxHeight: 600 }}
                        >
                          {partnerships.map((p, i) => {
                            const y = rowGap + i * (rowH + rowGap);
                            const p1Score = p.player1Score ?? 0;
                            const p2Score = p.player2Score ?? 0;
                            const leftW = barSize(p1Score);
                            const rightW = barSize(p2Score);
                            const p1Name = getPlayerNameById(p.playerId1);
                            const p2Name = getPlayerNameById(p.playerId2);
                            const p1Label = p1Score > 0 ? `${p1Name ?? 'Bat 1'} (${p1Score})` : (p1Name ?? 'Bat 1');
                            const p2Label = p2Score > 0 ? `${p2Name ?? 'Bat 2'} (${p2Score})` : (p2Name ?? 'Bat 2');
                            return (
                              <g key={i}>
                                <rect x={centerX - leftW} y={y} width={leftW} height={rowH} fill="#1d7a4b" />
                                <rect x={centerX} y={y} width={rightW} height={rowH} fill="#d4a017" />
                                {leftW > minBarWidthForInsideLabel ? (
                                  <text x={centerX - leftW + textPad} y={y + rowH / 2} dominantBaseline="middle" textAnchor="start" fontSize={12} fill="#fff">{p1Label}</text>
                                ) : (
                                  <text x={centerX - leftW - textPad} y={y + rowH / 2} dominantBaseline="middle" textAnchor="end" fontSize={12} fill="#333">{p1Label}</text>
                                )}
                                {rightW > minBarWidthForInsideLabel ? (
                                  <text x={centerX + rightW - textPad} y={y + rowH / 2} dominantBaseline="middle" textAnchor="end" fontSize={12} fill="#fff">{p2Label}</text>
                                ) : (
                                  <text x={centerX + rightW + textPad} y={y + rowH / 2} dominantBaseline="middle" textAnchor="start" fontSize={12} fill="#333">{p2Label}</text>
                                )}
                              </g>
                            );
                          })}
                          <line x1={centerX} y1={0} x2={centerX} y2={svgH} stroke="#999" strokeWidth={1} />
                        </svg>
                      );
                    })()}
                    {activeAnalysisTab === 'wagon' && wagonWheelBalls.length > 0 && (() => {
                      const svgW = 500;
                      const svgH = 420;
                      const fieldCx = svgW / 2;
                      const fieldCy = 190;
                      const fieldRx = 190;
                      const fieldRy = 155;
                      const stumpsX = fieldCx;
                      // Pitch vertically centred in inner oval; batter at the BOTTOM of the pitch
                      const pitchH = 90;
                      const pitchTopY = fieldCy - pitchH / 2; // bowler end
                      const stumpsY = pitchTopY + pitchH;     // batter end

                      // Distance along direction `angle` from stumps to outer ellipse boundary
                      const getBoundaryDist = (angle: number): number => {
                        const dirX = Math.sin(angle);
                        const dirY = -Math.cos(angle);
                        const dsx = stumpsX - fieldCx;
                        const dsy = stumpsY - fieldCy;
                        const a = (dirX * dirX) / (fieldRx * fieldRx) + (dirY * dirY) / (fieldRy * fieldRy);
                        const b = 2 * (dsx * dirX / (fieldRx * fieldRx) + dsy * dirY / (fieldRy * fieldRy));
                        const c = (dsx * dsx) / (fieldRx * fieldRx) + (dsy * dsy) / (fieldRy * fieldRy) - 1;
                        const disc = b * b - 4 * a * c;
                        if (disc < 0) return fieldRx;
                        return (-b + Math.sqrt(disc)) / (2 * a);
                      };

                      // Boundaries reach the edge; 1–3 run shots are scaled proportionally
                      const ballEndPoint = (angle: number, score: number) => {
                        const dirX = Math.sin(angle);
                        const dirY = -Math.cos(angle);
                        const t = getBoundaryDist(angle);
                        const fraction = score >= 4 ? 1 : score / 4;
                        return {
                          x: Math.round(stumpsX + t * dirX * fraction),
                          y: Math.round(stumpsY + t * dirY * fraction),
                        };
                      };

                      const ballColor = (score: number) =>
                        score >= 6 ? '#f97316' : score >= 4 ? '#3b82f6' : '#ffdd00';
                      const keyY = svgH - 30;

                      return (
                        <svg data-testid="wagon-wheel" viewBox={`0 0 ${svgW} ${svgH}`} className="w-full" style={{ maxHeight: 480 }}>
                          {/* Field */}
                          <ellipse cx={fieldCx} cy={fieldCy} rx={fieldRx} ry={fieldRy} fill="#4a8f3f" />
                          <ellipse cx={fieldCx} cy={fieldCy} rx={fieldRx * 0.5} ry={fieldRy * 0.5}
                            fill="#3a7f2f" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeDasharray="4 3" />
                          {/* Pitch — centred in oval, batter at bottom */}
                          <rect x={stumpsX - 7} y={pitchTopY} width={14} height={pitchH} fill="#c8a96e" rx="2" />
                          {/* Bowler direction arrow */}
                          <text x={stumpsX} y={pitchTopY - 32} textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize="13">Bowler</text>
                          <line x1={stumpsX} y1={pitchTopY - 27} x2={stumpsX} y2={pitchTopY - 8}
                            stroke="rgba(255,255,255,0.85)" strokeWidth="2" />
                          <polygon
                            points={`${stumpsX - 6},${pitchTopY - 8} ${stumpsX + 6},${pitchTopY - 8} ${stumpsX},${pitchTopY + 4}`}
                            fill="rgba(255,255,255,0.85)"
                          />
                          {/* Off/Leg labels — Off=right, Leg=left for right-handed batters */}
                          <text x={fieldCx + fieldRx * 0.55} y={stumpsY - 10} textAnchor="middle" fill="rgba(255,255,255,0.75)" fontSize="14">Off Side</text>
                          <text x={fieldCx - fieldRx * 0.55} y={stumpsY - 10} textAnchor="middle" fill="rgba(255,255,255,0.75)" fontSize="14">Leg Side</text>
                          {/* Batter stumps */}
                          <circle cx={stumpsX} cy={stumpsY} r={6} fill="white" />
                          {/* Shot lines */}
                          {wagonWheelBalls.map((ball, idx) => {
                            const angle = ball.angle!;
                            const rawScore = ball.amount ?? 0;
                            const score = ball.thing === 'nb' ? rawScore - 1 : rawScore;
                            if (score <= 0) return null;
                            const end = ballEndPoint(angle, score);
                            return (
                              <line key={idx} x1={stumpsX} y1={stumpsY} x2={end.x} y2={end.y} stroke={ballColor(score)} strokeWidth={2} strokeOpacity={0.85} />
                            );
                          })}
                          {/* Legend */}
                          <line x1={10} y1={keyY} x2={50} y2={keyY} stroke="#ffdd00" strokeWidth={4} />
                          <text x={55} y={keyY + 4} fontSize={13} fill="#333">Runs</text>
                          <line x1={110} y1={keyY} x2={150} y2={keyY} stroke="#3b82f6" strokeWidth={4} />
                          <text x={155} y={keyY + 4} fontSize={13} fill="#333">Fours</text>
                          <line x1={215} y1={keyY} x2={255} y2={keyY} stroke="#f97316" strokeWidth={4} />
                          <text x={260} y={keyY + 4} fontSize={13} fill="#333">Sixes</text>
                        </svg>
                      );
                    })()}
                    </div>
                  )}
                  </div>
                </section>
              );
            })()}
            {/* Player Analysis section */}
            {(data.completedOvers?.length ?? 0) > 0 && (() => {
              const allBalls = (data.completedOvers ?? []).flatMap(o => o.over?.balls ?? []);
              const isLegalDelivery = (ball: BallV1) => {
                const thing = ball.thing ?? '';
                return thing === '' || (thing === 'nb' && (ball.amount ?? 0) > 1);
              };
              const playerBallMap = new Map<number, { name: string; legalBalls: BallV1[]; allBalls: BallV1[] }>();
              allBalls.forEach(ball => {
                if (ball.batsman != null && ball.batsmanName) {
                  if (!playerBallMap.has(ball.batsman)) {
                    playerBallMap.set(ball.batsman, { name: ball.batsmanName, legalBalls: [], allBalls: [] });
                  }
                  const entry = playerBallMap.get(ball.batsman)!;
                  entry.allBalls.push(ball);
                  if (isLegalDelivery(ball)) {
                    entry.legalBalls.push(ball);
                  }
                }
              });
              const players = Array.from(playerBallMap.entries())
                .map(([id, { name, legalBalls, allBalls: pBalls }]) => ({ id, name, legalBalls, allBalls: pBalls }))
                .filter(p => p.legalBalls.length > 0);

              if (players.length === 0) return null;

              const getShortName = (name: string): string => {
                const parts = name.split(' ');
                let short = parts.map(p => p.charAt(0)).join('');
                if (short.length > 3) {
                  short = short.charAt(0) + short.charAt(1) + short.charAt(short.length - 1);
                }
                return short;
              };

              const effectivePlayerId = selectedPlayerId ?? players[0].id;
              const selectedPlayer = players.find(p => p.id === effectivePlayerId) ?? players[0];

              const playerWormPoints: { ball: number; score: number; sr: number }[] = [];
              let cumScore = 0;
              selectedPlayer.legalBalls.forEach((ball, idx) => {
                cumScore += ball.amount ?? 0;
                const ballNum = idx + 1;
                playerWormPoints.push({ ball: ballNum, score: cumScore, sr: (cumScore / ballNum) * 100 });
              });
              const maxSR = Math.max(...playerWormPoints.map(p => p.sr), 1);
              const finalScore = Math.max(cumScore, 1);
              const playerWormData = {
                labels: playerWormPoints.map(p => String(p.ball)),
                datasets: [
                  {
                    label: 'Score',
                    data: playerWormPoints.map(p => p.score),
                    borderColor: '#1d7a4b',
                    backgroundColor: 'transparent',
                    tension: 0.1,
                    pointRadius: 2,
                  },
                  {
                    label: 'Strike Rate (scaled)',
                    data: playerWormPoints.map(p => (p.sr / maxSR) * finalScore),
                    borderColor: '#d4a017',
                    backgroundColor: 'transparent',
                    tension: 0.1,
                    pointRadius: 2,
                  },
                ],
              };
              const playerWormOptions = {
                responsive: true,
                plugins: { legend: { position: 'top' as const } },
                scales: { x: { title: { display: true, text: 'Balls Faced' } } },
              };

              const playerWagonBalls = selectedPlayer.allBalls.filter(
                b => b.angle != null && isLegalDelivery(b)
              );

              return (
                <section className="max-w-6xl mx-auto mt-4">
                  <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <button
                    type="button"
                    className="w-full flex justify-between items-center px-4 py-3 text-left"
                    onClick={() => setPlayerAnalysisExpanded(prev => !prev)}
                    aria-expanded={playerAnalysisExpanded}
                  >
                    <span className="text-sm font-semibold text-gray-700">Player Analysis</span>
                    <svg
                      className={`w-4 h-4 text-gray-400 transition-transform ${playerAnalysisExpanded ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {playerAnalysisExpanded && (
                    <div className="border-t border-gray-100 px-4 py-4">
                      <div className="flex gap-2 mb-3 flex-wrap items-center">
                        {players.map(player => (
                          <button
                            key={player.id}
                            type="button"
                            onClick={() => setSelectedPlayerId(player.id)}
                            title={player.name}
                            aria-label={player.name}
                            className={`w-10 h-10 rounded-full text-xs font-bold flex items-center justify-center flex-shrink-0 border-2 ${
                              player.id === effectivePlayerId
                                ? 'bg-villageGreen text-white border-villageGreen'
                                : 'bg-white text-villageGreen border-villageGreen hover:bg-villageGreenLight'
                            }`}
                          >
                            {getShortName(player.name)}
                          </button>
                        ))}
                      </div>
                      <div className="flex gap-2 mb-3 flex-wrap">
                        <button
                          type="button"
                          onClick={() => setActivePlayerAnalysisTab('worm')}
                          className={`px-4 py-2 rounded-full text-sm font-medium ${
                            activePlayerAnalysisTab === 'worm'
                              ? 'bg-villageGreen text-white'
                              : 'border border-villageGreen text-villageGreen hover:bg-villageGreenLight'
                          }`}
                        >
                          Player Worm
                        </button>
                        {playerWagonBalls.length > 0 && (
                          <button
                            type="button"
                            onClick={() => setActivePlayerAnalysisTab('wagon')}
                            className={`px-4 py-2 rounded-full text-sm font-medium ${
                              activePlayerAnalysisTab === 'wagon'
                                ? 'bg-villageGreen text-white'
                                : 'border border-villageGreen text-villageGreen hover:bg-villageGreenLight'
                            }`}
                          >
                            Wagon Wheel
                          </button>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-gray-700 mb-3">{selectedPlayer.name}</p>
                      {activePlayerAnalysisTab === 'worm' && playerWormPoints.length > 0 && (
                        <Line data={playerWormData} options={playerWormOptions} />
                      )}
                      {activePlayerAnalysisTab === 'worm' && playerWormPoints.length === 0 && (
                        <p className="text-sm text-gray-500">No ball-by-ball data available for this player.</p>
                      )}
                      {activePlayerAnalysisTab === 'wagon' && (() => {
                          const svgW = 500;
                          const svgH = 420;
                          const fieldCx = svgW / 2;
                          const fieldCy = 190;
                          const fieldRx = 190;
                          const fieldRy = 155;
                          const stumpsX = fieldCx;
                          // Pitch vertically centred in inner oval; batter at the BOTTOM of the pitch
                          const pitchH = 90;
                          const pitchTopY = fieldCy - pitchH / 2; // bowler end
                          const stumpsY = pitchTopY + pitchH;     // batter end

                          // Distance along direction `angle` from stumps to outer ellipse boundary
                          const getBoundaryDist = (angle: number): number => {
                            const dirX = Math.sin(angle);
                            const dirY = -Math.cos(angle);
                            const dsx = stumpsX - fieldCx;
                            const dsy = stumpsY - fieldCy;
                            const a = (dirX * dirX) / (fieldRx * fieldRx) + (dirY * dirY) / (fieldRy * fieldRy);
                            const b = 2 * (dsx * dirX / (fieldRx * fieldRx) + dsy * dirY / (fieldRy * fieldRy));
                            const c = (dsx * dsx) / (fieldRx * fieldRx) + (dsy * dsy) / (fieldRy * fieldRy) - 1;
                            const disc = b * b - 4 * a * c;
                            if (disc < 0) return fieldRx;
                            return (-b + Math.sqrt(disc)) / (2 * a);
                          };

                          // Boundaries reach the edge; 1–3 run shots are scaled proportionally
                          const ballEndPoint = (angle: number, score: number) => {
                            const dirX = Math.sin(angle);
                            const dirY = -Math.cos(angle);
                            const t = getBoundaryDist(angle);
                            const fraction = score >= 4 ? 1 : score / 4;
                            return {
                              x: Math.round(stumpsX + t * dirX * fraction),
                              y: Math.round(stumpsY + t * dirY * fraction),
                            };
                          };

                          const ballColor = (score: number) =>
                            score >= 6 ? '#f97316' : score >= 4 ? '#3b82f6' : '#ffdd00';

                          const keyY = svgH - 30;

                          return (
                            <svg data-testid="player-wagon-wheel" viewBox={`0 0 ${svgW} ${svgH}`} className="w-full" style={{ maxHeight: 480 }}>
                              {/* Field */}
                              <ellipse cx={fieldCx} cy={fieldCy} rx={fieldRx} ry={fieldRy} fill="#4a8f3f" />
                              <ellipse cx={fieldCx} cy={fieldCy} rx={fieldRx * 0.5} ry={fieldRy * 0.5}
                                fill="#3a7f2f" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeDasharray="4 3" />
                              {/* Pitch — centred in oval, batter at bottom */}
                              <rect x={stumpsX - 7} y={pitchTopY} width={14} height={pitchH} fill="#c8a96e" rx="2" />
                              {/* Bowler direction arrow */}
                              <text x={stumpsX} y={pitchTopY - 32} textAnchor="middle" fill="rgba(255,255,255,0.85)" fontSize="13">Bowler</text>
                              <line x1={stumpsX} y1={pitchTopY - 27} x2={stumpsX} y2={pitchTopY - 8}
                                stroke="rgba(255,255,255,0.85)" strokeWidth="2" />
                              <polygon
                                points={`${stumpsX - 6},${pitchTopY - 8} ${stumpsX + 6},${pitchTopY - 8} ${stumpsX},${pitchTopY + 4}`}
                                fill="rgba(255,255,255,0.85)"
                              />
                              {/* Off/Leg labels — Off=right, Leg=left for right-handed batters */}
                              <text x={fieldCx + fieldRx * 0.55} y={stumpsY - 10} textAnchor="middle" fill="rgba(255,255,255,0.75)" fontSize="14">Off Side</text>
                              <text x={fieldCx - fieldRx * 0.55} y={stumpsY - 10} textAnchor="middle" fill="rgba(255,255,255,0.75)" fontSize="14">Leg Side</text>
                              {/* Batter stumps */}
                              <circle cx={stumpsX} cy={stumpsY} r={6} fill="white" />
                              {/* Shot lines */}
                              {playerWagonBalls.map((ball, idx) => {
                                const angle = ball.angle!;
                                const rawScore = ball.amount ?? 0;
                                const score = ball.thing === 'nb' ? rawScore - 1 : rawScore;
                                if (score <= 0) return null;
                                const end = ballEndPoint(angle, score);
                                return (
                                  <line key={idx} x1={stumpsX} y1={stumpsY} x2={end.x} y2={end.y} stroke={ballColor(score)} strokeWidth={2} strokeOpacity={0.85} />
                                );
                              })}
                              {/* Legend */}
                              <line x1={10} y1={keyY} x2={50} y2={keyY} stroke="#ffdd00" strokeWidth={4} />
                              <text x={55} y={keyY + 4} fontSize={13} fill="#333">Runs</text>
                              <line x1={110} y1={keyY} x2={150} y2={keyY} stroke="#3b82f6" strokeWidth={4} />
                              <text x={155} y={keyY + 4} fontSize={13} fill="#333">Fours</text>
                              <line x1={215} y1={keyY} x2={255} y2={keyY} stroke="#f97316" strokeWidth={4} />
                              <text x={260} y={keyY + 4} fontSize={13} fill="#333">Sixes</text>
                            </svg>
                          );
                      })()}
                    </div>
                  )}
                  </div>
                </section>
              );
            })()}


        {/* No result message */}
        {!completed && !live && !scorecardData.result?.isAbandoned && (
          <section className="max-w-6xl mx-auto mt-6 mb-10 bg-blue-50 border border-blue-200 rounded-lg p-6">
            <p className="text-blue-700">This match is scheduled but has not started yet.</p>
          </section>
        )}

        {/* Abandoned message */}
        {scorecardData.result?.isAbandoned && (
          <section className="max-w-6xl mx-auto mt-6 mb-10 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <p className="text-yellow-800 font-semibold">This match was abandoned.</p>
          </section>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default LiveScorecard;
